export const en = {
  common: {
    loading: 'Loading…',
    reloading: 'Reloading…',
    error: 'Something went wrong.',
    retry: 'Retry',
    close: 'Close',
    signOut: 'Sign out',
    create: 'Create',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    more: 'More',
    search: 'Search',
    copied: 'Copied',
    comingSoonBadge: 'Coming soon',
    // What a record is called when it has no name.
    untitledWorkspace: 'Untitled workspace',
    untitledAccount: 'Untitled account',
    unnamedPerson: 'Unnamed person',
    // A tab's label with how many entries it holds.
    tabWithCount: '{{label}} <count>{{count}}</count>',
    pagination: {
      label: 'Pagination',
      back: 'Back',
      next: 'Next',
    },
    table: {
      empty: 'Nothing here yet.',
      noMatches: 'Nothing matches “{{query}}”.',
    },
  },
  auth: {
    redirecting: 'Redirecting to sign in…',
  },
  nav: {
    label: 'Main',
    // The wordmark at the head of the rail, a link to the home page.
    home: 'Annwn home',
    devTools: 'Dev tools',
    // The rail's foot tile: where you are (the account, or the organisation
    // outside one), then who you are. It opens the account menu.
    accountMenu: '{{scope}}, {{person}}',
    switchAccount: 'Switch account',
    accounts: 'Accounts',
    users: 'Users',
    ontology: 'Ontology',
    whatsNew: 'What’s new',
    support: 'Support',
    toggleSidebar: 'Toggle sidebar',
  },
  dashboard: {
    greeting: {
      morning: 'Good morning, {{name}}.',
      afternoon: 'Good afternoon, {{name}}.',
      evening: 'Good evening, {{name}}.',
    },
    yourWorkspaces: 'Your workspaces',
    allWorkspaces: 'All workspaces',
  },
  account: {
    dashboard: 'Dashboard',
    workspaces: 'Workspaces',
    vault: 'Vault',
    corpus: 'Corpus',
    settings: 'Settings',
  },
  corpus: {
    title: 'Corpus',
    sectionsLabel: 'Corpus sections',
    tabs: {
      concepts: 'Concepts',
      dataset: 'Datasets',
      guidance: 'Guidance',
    },
    search: {
      concepts: 'Search concepts…',
      dataset: 'Search datasets…',
      guidance: 'Search guidance…',
    },
    themesLabel: 'Theme',
    allThemes: 'All themes',
    themes: {
      carbon: 'Carbon',
      water: 'Water',
      supply_chain: 'Supply chain',
      assurance_controls: 'Assurance & controls',
      reporting_disclosure: 'Reporting & disclosure',
      regulatory_claims: 'Regulatory & claims',
    },
    provenance: {
      authoritative: 'Authoritative',
      derived: 'Derived',
    },
    status: {
      draft: 'Draft',
      in_use: 'In use',
    },
    licences: {
      open_attribution: 'Open, with attribution',
      licensed: 'Licensed',
      unknown: 'Licence unknown',
    },
    // Whether Annwn keeps a copy of a holding.
    storage: {
      held: 'Stored by Annwn',
      cited: 'Cited, not stored',
    },
    sample: 'Sample',
    samples: 'Samples',
    noMatches: 'Nothing matches.',
    empty: {
      concepts: 'No concepts yet.',
      dataset: 'No datasets yet.',
      guidance: 'No questionnaire guidance yet.',
    },
    // A platform questionnaire's guidance.
    guidance: {
      name: '{{questionnaire}} guidance',
      allGuidance: 'All guidance',
      facts: {
        modules: 'Modules',
        questions: 'Questions',
      },
      moduleSelect: 'Module',
      search: 'Search questions and guidance…',
      searching: 'Searching every module…',
      matches_one: '{{count}} question matches',
      matches_other: '{{count}} questions match',
      // In place of the count when the search stopped short of every match.
      truncated:
        'More than {{shown}} questions match, so only the first {{shown}} are shown. Try a more specific term.',
      noMatches: 'No guidance matches.',
      empty: 'This questionnaire has no modules yet.',
    },
    // A concept document's facts, under its title.
    facts: {
      version: 'Version',
      status: 'Status',
      failureModes: 'Failure modes',
    },
    item: {
      provenance: 'Authority',
      status: 'Status',
      publisher: 'Publisher',
      version: 'Version',
      licence: 'Licence',
      holding: 'Copy',
      citedBy: 'Cited by',
      attribution: 'Attribution: {{text}}',
      viewAtPublisher: 'View at the publisher',
    },
    version: 'v{{version}}',
    allConcepts: 'All concepts',
    contents: 'Contents',
    definition: 'Definition',
    // A numbered section's heading, as its document gives it.
    sectionNumber: '{{number}}. {{title}}',
    // Concept ids. A concept with no entry shows its id made readable.
    concepts: {
      energy_consumption: 'Energy consumption',
    },
    // Activity `type` codes of the ontology. A type with no entry shows its
    // code made readable.
    activityTypes: {
      energy_consumption: 'Energy consumption',
    },
    stored: {
      title: 'What we have stored',
      activities: 'Activities',
      activityType: '{{type}} activities',
      figures: 'Figures',
      measures_one: 'measure',
      measures_other: 'measures',
      values_one: '{{count}} value recorded',
      values_other: '{{count}} values recorded',
      valuesInUnits_one: '{{count}} value recorded, in {{units}}',
      valuesInUnits_other: '{{count}} values recorded, in {{units}}',
      examples: 'For example: {{names}}',
      nothing: {
        title: 'Nothing stored yet',
        body: 'None of this account’s documents has given Annwn any data for this concept. Upload documents to a workspace and their data appears here once it is extracted.',
      },
      unbound: {
        title: 'Not read for this account yet',
        body: 'This account uses ontology {{version}}, which this concept has no mapping for, so Annwn’s agents can’t find its data here.',
      },
    },
  },
  accountSettings: {
    title: 'Settings',
    modelProviders: {
      title: 'Language models',
      description:
        'Which AI providers may run analysis and drafting for this account.',
      useDefault: 'Use platform default',
      useDefaultHint: 'Annwn may use any provider it supports.',
      restrict: 'Restrict to specific providers',
      restrictHint: 'Only the providers you select will be used.',
      required: 'Select at least one provider.',
      selectLabel: 'Allowed providers',
      selectPlaceholder: 'Select providers',
      access: {
        external: 'External',
        hosted: 'Hosted',
      },
    },
    embeddings: {
      title: 'Embeddings',
      description:
        'The provider used to index and search this account’s documents.',
      selectLabel: 'Embedding provider',
      useDefault: 'Use platform default',
      useDefaultHint: 'Annwn uses its configured embedding provider.',
      migrating: 'Migration in progress',
      migrationInFlight:
        "A provider migration is already in progress; you can't change the target until it finishes.",
      rerun: 'Re-run migration',
      rerunHint: 'Re-send the request if the migration seems stuck.',
      rerunPending: 'Requesting…',
      rerunConfirmed: 'Migration re-requested — this can take a few minutes.',
    },
    agentConfiguration: {
      title: 'Agent configuration',
      description:
        'Each agent runs at its default model tier unless you override it here. Larger tiers are slower and cost more.',
      empty: 'Every agent runs at its default tier.',
      add: 'Add override',
      search: 'Search agents',
      noMatches: 'No agents match.',
      remove: 'Remove the {{agent}} override',
      defaultTier: 'Default: {{tier}}',
      sameAsDefault: 'Same as the default',
      // The Add override picker's column headings, and each option's name for
      // screen readers.
      pickerAgent: 'Agent',
      pickerDefault: 'Default tier',
      pickerOption: '{{agent}}, {{tier}} by default',
      tiers: { small: 'Small', medium: 'Medium', large: 'Large' },
      // Keyed by the `GET /model-agents` id. An agent with no entry shows its id.
      agents: {
        'classify:classifier': 'Document classification',
        'extract:extractor': 'Extraction',
        'extract:deduper': 'Extraction de-duplication',
        'extract:cdp_response': 'CDP response extraction',
        'reconcile:matcher': 'Reconciliation matching',
        'reconcile:cluster_reconcile': 'Reconciliation clustering',
        'draft:drafter': 'Drafting',
        'chat:responder': 'Ask Annwn replies',
        'chat:planner': 'Ask Annwn planning',
        'agent:author': 'Author agent',
        'agent:curate': 'Curate agent',
        'agent:analyse': 'Analyse agent',
        'agent:shape': 'Shape agent',
        'agent:review': 'Review agent',
        'concepts:classifier': 'Concept classification',
      },
    },
    ontology: {
      title: 'Ontology',
      description:
        'The version of Annwn’s knowledge-graph schema this account’s analysis and drafting run against.',
      versionLabel: 'Version {{number}}',
      defaultOption: '{{label}} (Default)',
      unsupportedOption: 'Unsupported version',
      unsupportedNotice:
        'This account is set to {{label}}, which this platform no longer supports. Your other settings still save normally, and this one is left untouched unless you pick a supported version — nothing already extracted is migrated.',
      switchWarning:
        'Applies to future runs only. Data already extracted under {{version}} is not migrated, so this account’s graph will hold both versions.',
      confirmTitle: 'Change ontology version?',
      confirmBody:
        'This account runs against {{to}} from its next analysis or drafting run. Data extracted under {{from}} is not migrated and no re-index is triggered, so the graph will hold both — and because only runs record the version, there is no scoped rollback. Prefer a fresh account for a first trial.',
      confirmAction: 'Change version',
    },
    providers: {
      anthropicDirect: 'Anthropic (Direct)',
      openaiDirect: 'OpenAI (Direct)',
      geminiOnVertex: 'Google Gemini (via Vertex AI)',
      anthropicOnBedrock: 'Anthropic (via AWS Bedrock)',
      openaiOnBedrock: 'OpenAI (via AWS Bedrock)',
      cohereOnBedrock: 'Cohere (via AWS Bedrock)',
      glmOnBedrock: 'Z.AI GLM (via AWS Bedrock)',
      kimiOnBedrock: 'Moonshot AI Kimi (via AWS Bedrock)',
      qwenOnBedrock: 'Qwen (via AWS Bedrock)',
      minimaxOnBedrock: 'MiniMax (via AWS Bedrock)',
      openaiOssOnBedrock: 'OpenAI OSS (via AWS Bedrock)',
      // Bare vendor names for the grouped picker, where the External/Hosted
      // heading already states the access route. The qualified names above are
      // still used wherever there is no heading to carry it — the picker's
      // summary line, and the ungrouped embedding radios.
      short: {
        anthropicDirect: 'Anthropic',
        openaiDirect: 'OpenAI',
        geminiOnVertex: 'Google Gemini',
        anthropicOnBedrock: 'Anthropic',
        openaiOnBedrock: 'OpenAI',
        glmOnBedrock: 'Z.AI GLM',
        kimiOnBedrock: 'Moonshot AI Kimi',
        qwenOnBedrock: 'Qwen',
        minimaxOnBedrock: 'MiniMax',
        openaiOssOnBedrock: 'OpenAI OSS',
        cohereOnBedrock: 'Cohere',
      },
    },
    // Where a provider runs inference. Short forms: they sit beside a flag in
    // a menu row, where a full name would crowd the provider's own.
    regions: {
      global: 'Global',
      us: 'US',
      europe: 'EU',
      uk: 'UK',
    },
    save: 'Save changes',
    discard: 'Discard',
    saveError: 'Could not save your changes. Please try again.',
    noAccess: 'You do not have access to configure this account.',
    preferencesUnreadable:
      'This account’s stored settings could not be read — they refer to an option the platform no longer supports. That has to be corrected on the platform before these settings can be changed.',
  },
  workspace: {
    overview: 'Overview',
    review: 'Review',
    deliverables: 'Deliverables',
    reviewComingSoon:
      'Review will let you check the generated answers in depth and flag anything that needs another look — the quality controls that make sure the work holds up before it goes out.',
    deliverablesComingSoon:
      'Deliverables will be where you generate the finished outputs and export the data you need back out of the workspace.',
    applicability: {
      title: 'Edition and sections',
      edition: 'Edition',
      editionFixed: 'Set when the workspace was created.',
      selectAll: 'Select all',
      clear: 'Clear',
      save: 'Save selection',
      discard: 'Discard changes',
      saveError: 'Could not update your selection. Please try again.',
      empty: 'This questionnaire has no configurable sections.',
      noQuestionnaire: 'No questionnaire is assigned to this workspace yet.',
      noAccess: 'You do not have access to configure this workspace.',
      requiredVersion: 'Choose a questionnaire version.',
      requiredSection: 'Select at least one.',
      showAll: 'Show all questions',
      filteringOff:
        'Filtering is off — every question in the questionnaire is shown. Choose a version and the options that apply, then save to narrow it.',
      showAllConfirmTitle: 'Show all questions?',
      showAllConfirmBody:
        'This clears the applicability selection for this workspace, so every question becomes visible. You can reconfigure at any time.',
      showAllConfirmAction: 'Clear selection',
      groups: {
        version: 'Questionnaire version',
        sectors: 'Sectors',
        questions: 'Question types',
        environmental: 'Environmental issues',
        mining: 'Biodiversity (mining projects)',
      },
      // One line under each step's heading on what the choice decides.
      descriptions: {
        version: 'The questionnaire CDP has asked your organisation to answer.',
        sectors:
          'Sector questions appear only for the sectors you choose. Pick every sector you report on.',
        questions:
          'Questions that apply only when you are responding to one of these requests. Leave them all off if none apply.',
        environmental:
          'The issues you are disclosing on. Each one brings in its own questions.',
        mining: 'Questions for mining projects that bear on biodiversity.',
      },
      options: {
        fullVersion: 'Full version',
        fullVersionHint: 'The complete questionnaire for this edition.',
        smeQuestionnaire: 'SME questionnaire',
        smeUnavailable:
          'For small and medium-sized organisations. Not available yet.',
        allOtherSectors: 'All other sectors',
        supplyChain: 'Supply Chain',
        re100: 'RE100',
        publicAuthorities: 'Public authorities',
        climateChange: 'Climate change',
        forests: 'Forests',
        water: 'Water',
        ocean: 'Ocean',
        plastics: 'Plastics',
        biodiversity: 'Biodiversity',
        biodiversityMines: 'Biodiversity – Mines',
      },
    },
  },
  workspaceNav: {
    // The workspace's working flow, in order; review and deliver are still to come.
    steps: {
      shape: { label: 'Shape', hint: 'Edition and sections' },
      curate: { label: 'Curate', hint: 'Documents' },
      author: { label: 'Author', hint: 'Questionnaire' },
      review: { label: 'Review', hint: 'Soon' },
      deliver: { label: 'Deliver', hint: 'Soon' },
    },
    label: 'Workspace navigation',
    stepList: 'Steps',
    toggle: 'Toggle workspace navigation',
    sections: 'Sections',
    switchWorkspace: 'Switch workspace',
    allQuestions: 'All questions',
    allWorkspaces: 'All workspaces',
  },
  dock: {
    move: 'Move {{pane}} pane',
    close: 'Close {{pane}} pane',
    resize: 'Resize {{before}} and {{after}}',
    roleDescription: 'movable pane',
    instructions:
      'To move this pane, press Space or Enter, choose where it goes with the arrow keys, then press Space or Enter to drop it. Press Escape to cancel.',
    placement: {
      leftOf: 'left of {{other}}',
      rightOf: 'right of {{other}}',
      above: 'above {{other}}',
      below: 'below {{other}}',
    },
    announce: {
      start: 'Picked up the {{pane}} pane.',
      over: '{{pane}} pane: {{placement}}.',
      home: '{{pane}} pane: where it started.',
      dropped: 'Moved the {{pane}} pane {{placement}}.',
      droppedHome: 'Dropped the {{pane}} pane where it started.',
      cancelled: 'Move cancelled. The {{pane}} pane is back where it started.',
    },
    menu: {
      trigger: 'Arrange {{pane}} pane',
      label: 'Move this pane',
      left: 'Move left',
      right: 'Move right',
      above: 'Move above {{other}}',
      below: 'Move below {{other}}',
      ownColumn: 'Own column',
    },
  },
  draft: {
    title: 'Draft',
    allModules: 'All modules',
    filterLabel: 'Filter by status',
    statusAll: 'All',
    columns: {
      id: 'ID',
      status: 'Status',
      question: 'Question',
    },
    emptyFiltered: 'No questions match this filter.',
    noQuestionnaireTitle: 'No questionnaire yet',
    noQuestionnaire: 'No questionnaire is attached to this workspace yet.',
  },
  accounts: {
    title: 'Accounts',
    status: {
      active: 'Active',
      inactive: 'Inactive',
      archived: 'Archived',
    },
    new: 'New account',
    rename: 'Rename account',
    empty: 'No accounts yet.',
    usage: '{{used}} of {{limit}} used',
    atLimit: 'Account limit reached for your organisation.',
    manageAll: 'Manage all accounts',
    deleteTitle: 'Delete account?',
    deleteDescription:
      'This will delete the “{{name}}” account and all of its workspaces. This cannot be undone.',
    fields: {
      name: 'Name',
      namePlaceholder: 'Account name',
    },
    emptyBody:
      'Each organisation you work for gets an account of its own, with its workspaces and documents kept apart.',
  },
  users: {
    title: 'Users',
    new: 'New user',
    empty: 'No users yet.',
    searchPlaceholder: 'Search by name',
    actionsLabel: 'Actions',
    unknown: '—',
    columns: {
      name: 'Name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      createdAt: 'Created',
      lastSignedIn: 'Last signed in',
    },
    status: {
      active: 'Active',
      invited: 'Invited',
      provisional: 'Inactive',
      suspended: 'Suspended',
      deactivated: 'Deactivated',
    },
    roles: {
      contributor: 'Contributor',
      reviewer: 'Reviewer',
      org_admin: 'Administrator',
      internal_admin: 'Internal Admin',
    },
    actions: {
      invite: 'Send invitation',
      resend: 'Resend invitation',
      revoke: 'Revoke invitation',
      suspend: 'Suspend user',
    },
    createDialog: {
      title: 'New user',
      description:
        'Add someone to your organisation. They can be assigned to questions before they onboard.',
      emailLabel: 'Email',
      emailPlaceholder: 'name@company.com',
      nameLabel: 'Display name',
      namePlaceholder: 'Jane Doe',
      roleLabel: 'Role',
      rolePlaceholder: 'Select a role',
      inviteNow: 'Send invitation now',
      submit: 'Create',
    },
    errors: {
      duplicate: 'A user with that email already exists in your organisation.',
      generic: 'Something went wrong. Please try again.',
      inviteFailed:
        'The user was created, but the invitation could not be sent. You can resend it from the list.',
    },
    suspendTitle: 'Suspend user?',
    suspendDescription:
      '“{{name}}” will be suspended and lose access to the organisation. Their existing work stays attributed to them, and any pending invitation is revoked.',
    suspendConfirm: 'Suspend',
  },
  documents: {
    title: 'Documents',
    allDocuments: 'All documents',
    searchPlaceholder: 'Search',
    uploadDescription:
      'annwn will draft answers from your annual report, CDP responses, and more.',
    addDocs: 'Add docs',
    actionsLabel: 'Actions',
    emptyTable: 'No documents uploaded yet.',
    // The line beside a document's status in the table, for assistive tech.
    statusProgressLabel: 'Processing progress',
    statusLabels: {
      processing: 'Processing',
    },
    failureReason: {
      llmProviderError:
        'A temporary service issue occurred. Try uploading again.',
      persistError:
        'The document was processed but could not be saved. Try again.',
      internalError:
        'An unexpected error occurred while processing this document.',
      fileTooLarge: 'This document is too large to process.',
      documentEmpty: 'This document appears to be empty.',
      documentUnreadable:
        'This document could not be read - it may be corrupted.',
      unsupportedFileType: "This file type isn't supported.",
    },
  },
  documentTable: {
    columns: {
      name: 'Document',
      status: 'Status',
      uploadedBy: 'Uploaded by',
      uploaded: 'Uploaded',
      actions: 'Actions',
    },
    unknownUploader: '—',
    graph: {
      view: 'View graph',
      viewFor: 'View graph for {{name}}',
    },
  },
  documentPreview: {
    close: 'Close preview',
    download: 'Download',
    downloadFailed: 'Couldn’t get the file. Try again.',
    uploaded: 'Uploaded {{date}}',
    uploadedBy: 'Uploaded {{date}} by {{name}}',
    pages_one: '{{count}} page',
    pages_other: '{{count}} pages',
    sheets_one: '{{count}} sheet',
    sheets_other: '{{count}} sheets',
    sheetsLabel: 'Sheets',
    pageLabel: 'Page {{page}}',
    emptySheet: 'This sheet is empty.',
    truncated:
      'This preview shows up to {{rows}} rows and {{columns}} columns. Download the file to see all of it.',
    tooLarge: 'Too large to preview',
    unavailable: {
      title: 'No preview for this file type yet',
      body: 'Download it to open it in its own app.',
    },
    processing: {
      title: 'Still being read',
      body: 'The preview appears once Annwn has finished with this document.',
    },
    failed: {
      title: 'This document couldn’t be processed',
    },
    error: {
      title: 'Couldn’t load the preview',
      body: 'Something went wrong fetching this document. Try again in a moment.',
    },
  },
  vault: {
    title: 'Vault',
    searchPlaceholder: 'Search documents',
    partialError: 'Documents from {{workspaces}} couldn’t be loaded.',
    emptyTitle: 'Nothing in the vault yet',
    empty:
      'Documents uploaded to any workspace in this account will appear here.',
    goToWorkspaces: 'Go to workspaces',
    noMatches: 'No documents match these filters.',
    clearFilters: 'Clear filters',
    columns: {
      workspace: 'Workspace',
    },
    filters: {
      allWorkspaces: 'All workspaces',
      status: 'Status',
      allStatuses: 'All statuses',
      type: 'Type',
      allTypes: 'All types',
    },
    kinds: {
      pdf: 'PDFs',
      spreadsheet: 'Spreadsheets',
      csv: 'CSV files',
      document: 'Documents',
      presentation: 'Presentations',
      image: 'Images',
      text: 'Text files',
      other: 'Other files',
    },
  },
  workspaces: {
    title: 'Workspaces',
    new: 'New workspace',
    edit: 'Edit workspace',
    empty: 'No workspaces yet.',
    emptyBody:
      'A workspace holds one piece of work — a CDP response, a board briefing — with its evidence beside it. Start one when you’re ready.',
    noMatches: 'No workspaces match your search.',
    noStatusMatches: 'No workspaces have this status.',
    noFilteredMatches: 'No workspaces with this status match your search.',
    actionsFor: 'Actions for {{name}}',
    // A workspace card: when its questionnaire is due, and its progress.
    card: {
      due: 'Due {{date}}',
      dueToday: 'Due today',
      dueIn_one: 'Due tomorrow',
      dueIn_other: 'Due in {{count}} days',
      overdue: 'Overdue since {{date}}',
      // The status hairline, said to a screen reader: "Answered 93 of 214:
      // Done 23, In progress 52, AI update 18, Not started 121".
      breakdown: 'Answered {{answered}} of {{total}}: {{parts}}',
      breakdownPart: '{{status}} {{figure}}',
    },
    searchPlaceholder: 'Search by name…',
    allStatuses: 'All statuses',
    statusFilterLabel: 'Filter by status',
    status: {
      created: 'Created',
      initialised: 'Ready to start',
      active: 'In progress',
      complete: 'Complete',
      archived: 'Archived',
    },
    categories: {
      reporting: 'Reporting & Disclosure',
      carbon: 'Carbon & GHG Accounting',
      targets: 'Targets & Strategy',
      ratings: 'Ratings & Questionnaires',
      supplyChain: 'Supply Chain & Sourcing',
      nature: 'Nature & Biodiversity',
      water: 'Water Stewardship',
      waste: 'Waste & Circularity',
      social: 'Social & Human Rights',
      assurance: 'Assurance & Audit',
      advisory: 'Advisory & Briefings',
      regulatory: 'Regulatory & Compliance',
    },
    soon: 'Soon',
    whatsAWorkspace: 'What’s a workspace?',
    explainer: {
      title: 'What is a workspace?',
      description:
        'A workspace is where one job of work gets done — from a quick board briefing to a full CDP submission. It holds the scope, the evidence, the agent team and the deliverable for that job, in one place.',
      pathEyebrow: 'Every workspace follows the same path',
      modes: {
        shape: {
          title: 'Shape',
          body: 'Define the outcome, who’s involved, and what needs to get done.',
        },
        curate: {
          title: 'Curate',
          body: 'Pull in the right data, internal and external. Find what is missing.',
        },
        author: {
          title: 'Author',
          body: 'Produce the response, analysis, or artefact — and the deliverables.',
        },
        review: {
          title: 'Review',
          body: 'Check quality, calibrate, and prepare for external audit.',
        },
        deploy: {
          title: 'Deploy',
          body: 'Submit, send, share.',
        },
      },
      differentiators: {
        agents: {
          title: 'An agent team works inside it.',
          body: 'Each workspace briefs Annwn agents on that specific job — they gather evidence, draft, check and flag what needs you.',
        },
        knowledge: {
          title: 'It draws on everything you know.',
          body: 'Workspaces share your organisation’s knowledge graph — an answer produced once is reused everywhere, with provenance back to its source.',
        },
        shaped: {
          title: 'It’s shaped to the job.',
          body: 'A ratings submission, a transition plan and a board briefing each specialise the same path — same way of working, different deliverable.',
        },
      },
    },
    createDialog: {
      liveCount: '{{live}} of {{total}} live',
      start: 'Start',
      startItem: 'Start {{name}}',
      shapeTitle: 'Let’s shape the work',
      shapeDescription:
        'Just the essentials to get going — you’ll flesh out scope, data and owners inside the workspace.',
      nameLabel: 'Name this work',
      cycleLabel: 'Reporting cycle',
      cyclePlaceholder: 'Select a cycle',
      back: 'Back',
    },
    templateCatalogue: {
      cdpClimate: 'CDP Climate Disclosure',
      csrdEsrs: 'CSRD / ESRS Reporting',
      doubleMateriality: 'Double Materiality Assessment',
      tcfd: 'Climate Risk (TCFD)',
      esgInvestor: 'ESG Investor Reporting',
      griReport: 'Sustainability Report (GRI)',
      issbS1S2: 'ISSB S1 & S2 Disclosure',
      annualReportChapter: 'Annual Report Chapter',
      ukSrs: 'UK SRS Disclosure',
      sustainabilityLinkedFinance: 'Sustainability-Linked Finance',
      ghgInventory: 'GHG Inventory',
      energyManagement: 'Energy Management',
      productCarbonFootprint: 'Product Carbon Footprint',
      scope3DeepDive: 'Scope 3 Deep Dive',
      secr: 'SECR Reporting',
      renewableEnergy: 'Renewable Energy Procurement',
      carbonCredits: 'Carbon Credit Procurement',
      decarbStrategy: 'Decarbonisation Strategy',
      climateAdaptation: 'Climate Adaptation',
      netZeroRoadmap: 'Net Zero Roadmap',
      transitionPlan: 'Climate Transition Plan',
      sbti: 'SBTi Target Validation',
      internalCarbonPricing: 'Internal Carbon Pricing',
      customerRfq: 'Customer RFQ Response',
      ecovadis: 'EcoVadis Submission',
      rfpTender: 'RFP & Tender Response',
      supplierEngagement: 'Supplier Engagement',
      sustainableProcurement: 'Sustainable Procurement',
      csddd: 'CSDDD Due Diligence',
      tnfdLeap: 'TNFD Disclosure (LEAP)',
      biodiversityLandUse: 'Biodiversity & Land Use',
      eudr: 'EUDR Due Diligence',
      waterStewardship: 'Water Stewardship Programme',
      waterRisk: 'Water Risk Assessment',
      contextWaterTargets: 'Context-Based Water Targets',
      wasteCircular: 'Waste & Circular Economy',
      packagingEpr: 'Packaging EPR',
      plasticsPackaging: 'Plastics & Packaging Strategy',
      socialImpact: 'Social Impact Assessment',
      justTransition: 'Just Transition',
      modernSlavery: 'Modern Slavery Statement',
      hrdd: 'Human Rights Due Diligence',
      payGap: 'Pay Gap Reporting',
      assuranceReadiness: 'Assurance Readiness Pack',
      esgDataControls: 'ESG Data Controls',
      iso14001: 'ISO 14001 / 50001',
      quickReport: 'Quick Report',
      meetingPrep: 'Meeting Preparation',
      governanceEthics: 'Governance & Ethics Review',
      responsibleAi: 'Responsible AI',
      maDueDiligence: 'M&A ESG Due Diligence',
      policyDrafting: 'Policy Drafting & Refresh',
      horizonScan: 'Regulatory Horizon Scan',
      euTaxonomy: 'EU Taxonomy Alignment',
      cbam: 'CBAM Compliance',
      greenClaims: 'Green Claims Review',
      esos: 'ESOS Energy Audit',
    },
    templateDescriptions: {
      cdpClimate:
        'Answer the CDP climate questionnaire, drafted from your own evidence.',
      csrdEsrs: 'Prepare your CSRD sustainability statement against the ESRS.',
      doubleMateriality:
        'Work out which impacts, risks and opportunities matter most to you.',
      tcfd: 'Set out your climate risks and opportunities in the TCFD structure.',
      esgInvestor: 'Answer the ESG questions your investors send you.',
      griReport: 'Write your sustainability report to the GRI Standards.',
      issbS1S2:
        'Disclose sustainability and climate information under IFRS S1 and S2.',
      annualReportChapter:
        'Draft the sustainability chapter of your annual report.',
      ukSrs:
        'Prepare your disclosures under the UK Sustainability Reporting Standards.',
      sustainabilityLinkedFinance:
        'Track and report the KPIs tied to your sustainability-linked finance.',
      ghgInventory: 'Build your scope 1, 2 and 3 emissions inventory.',
      energyManagement: 'See where you use energy and where you can cut it.',
      productCarbonFootprint: 'Measure the emissions behind a single product.',
      scope3DeepDive:
        'Find out where your value-chain emissions really come from.',
      secr: 'Prepare your UK Streamlined Energy and Carbon Reporting.',
      renewableEnergy: 'Plan how you’ll buy renewable electricity.',
      carbonCredits: 'Choose carbon credits you can stand behind.',
      decarbStrategy: 'Plan how you’ll bring your emissions down, and by when.',
      climateAdaptation:
        'Assess how a changing climate affects you, and plan your response.',
      netZeroRoadmap: 'Map the steps from where you are today to net zero.',
      transitionPlan:
        'Write a climate transition plan your stakeholders can trust.',
      sbti: 'Prepare your science-based targets for SBTi validation.',
      internalCarbonPricing:
        'Set a carbon price to guide your own investment decisions.',
      customerRfq: 'Answer the sustainability questions in a customer’s RFQ.',
      ecovadis:
        'Prepare your EcoVadis questionnaire and the evidence behind it.',
      rfpTender: 'Answer the sustainability sections of a bid or tender.',
      supplierEngagement:
        'Bring your suppliers with you on emissions and data.',
      sustainableProcurement: 'Build sustainability into how you buy.',
      csddd: 'Prepare for due diligence under the EU CSDDD.',
      tnfdLeap:
        'Work through the TNFD LEAP approach for your nature-related risks.',
      biodiversityLandUse: 'Understand your impact on land and biodiversity.',
      eudr: 'Show your products are deforestation-free under the EUDR.',
      waterStewardship:
        'Plan how you’ll look after the water you share with others.',
      waterRisk: 'Find where water risk touches your sites and supply chain.',
      contextWaterTargets:
        'Set water targets that fit the basins you draw from.',
      wasteCircular: 'Cut waste and keep materials in use for longer.',
      packagingEpr:
        'Report your packaging under extended producer responsibility rules.',
      plasticsPackaging:
        'Plan how you’ll reduce and replace plastic packaging.',
      socialImpact:
        'Assess the effect your work has on people and communities.',
      justTransition:
        'Plan a transition that is fair to workers and communities.',
      modernSlavery: 'Write your annual modern slavery statement.',
      hrdd: 'Find and address human rights risks in your operations and supply chain.',
      payGap: 'Report your gender and ethnicity pay gaps.',
      assuranceReadiness: 'Get your data and evidence ready for an assurer.',
      esgDataControls:
        'Put checks around how your ESG data is collected and signed off.',
      iso14001: 'Prepare for ISO 14001 or ISO 50001 certification.',
      quickReport: 'Turn a question into a short, sourced report.',
      meetingPrep: 'Get briefed before a board, investor or customer meeting.',
      governanceEthics:
        'Review how well your governance and ethics policies hold up.',
      responsibleAi: 'Set out how you use AI responsibly.',
      maDueDiligence: 'Assess the ESG risks of a deal before you sign.',
      policyDrafting: 'Draft a new policy or bring an old one up to date.',
      horizonScan:
        'See which regulations are coming and what they mean for you.',
      euTaxonomy: 'Assess how your activities align with the EU Taxonomy.',
      cbam: 'Report the embedded emissions in goods you import into the EU.',
      greenClaims: 'Check your environmental claims will stand up to scrutiny.',
      esos: 'Prepare your ESOS energy audit.',
    },
    deleteTitle: 'Delete workspace?',
    deleteDescription:
      'This will delete the “{{name}}” workspace. This cannot be undone.',
    fields: {
      name: 'Name',
      namePlaceholder: 'Workspace name',
      description: 'Description',
      descriptionPlaceholder: 'What is this workspace for?',
    },
    open: 'Open',
  },
  uploads: {
    // Where a dropped document goes: a workspace's documents.
    where: '{{workspace}} · Documents',
    addTo: 'Add to {{where}}',
    dropTo: 'Drop to add to {{where}}',
    choose: 'Drag documents here, or click to choose them',
    laneLabel: 'Arriving in {{where}}',
    trayLabel: 'Uploads',
    // The tray's heading, by the state of the uploads in it.
    title: {
      uploading_one: 'Uploading {{count}} document',
      uploading_other: 'Uploading {{count}} documents',
      processing_one: 'Reading {{count}} document',
      processing_other: 'Reading {{count}} documents',
      ready: 'Uploads ready',
      failed: 'An upload failed',
    },
    // One word per stage a document passes through, in order.
    steps: {
      uploading: 'Uploading',
      queued: 'Queued',
      reading: 'Reading',
      classifying: 'Classifying',
      extracting: 'Extracting',
      reconciling: 'Reconciling',
      indexing: 'Indexing',
      ready: 'Ready',
      failed: 'Failed',
    },
    progressLabel: 'Progress of {{name}}',
    stageOf: '{{step}}, stage {{current}} of {{total}}',
    uploadFailed: 'The upload didn’t finish. Try again.',
    cancel: 'Cancel the upload of {{name}}',
    dismissRow: 'Dismiss {{name}}',
    // A destination's uploads as a whole, where there is no room for a row
    // per file (the workspace overview).
    summary: {
      progressLabel: 'Progress of these uploads',
      ready_one: '{{count}} ready',
      ready_other: '{{count}} ready',
      failed_one: '{{count}} failed',
      failed_other: '{{count}} failed',
      view: 'View documents',
    },
    dismiss: 'Dismiss',
    expand: 'Expand',
    collapse: 'Collapse',
  },
  questionnaires: {
    stats: {
      regionLabel: 'Questionnaire summary',
      completed: 'Completed',
      readyForReview: 'Ready for review',
      aiUpdates: 'AI updates',
      unassigned: 'Unassigned',
      assignedToMe: 'Assigned to me',
      daysToDeadline: 'Days to deadline',
      daysSuffix_one: '{{count}} day',
      daysSuffix_other: '{{count}} days',
      noDeadline: '—',
    },
    heatmap: {
      title: 'Completeness',
      questionAriaLabel: '{{code}} — {{status}}',
      questionTextFallback: 'Untitled question',
      sectionTitleFallback: 'Untitled section',
      filter: {
        all: 'All',
        assignedToMe: 'Assigned to me',
        status: 'Status',
        anyStatus: 'Any status',
      },
    },
    statusLabel: {
      notStarted: 'Not started',
      aiUpdate: 'AI update',
      inProgress: 'In progress',
      needsReview: 'Needs review',
      inReview: 'In review',
      // The `conflict` question status renders as the Figma "Issue" state.
      conflict: 'Issue',
      done: 'Done',
    },
    // The per-question header status pill — a dropdown that moves the question
    // through its review lifecycle: mark ready for review, mark done (sign-off),
    // or reopen a signed-off answer. The reviewer's approve / request-changes
    // actions live in the in-review banner (see responseReview below).
    responseStatus: {
      menuLabel: 'Change question status',
      markReady: 'Mark as ready for review',
      markDone: 'Mark as done',
      reopen: 'Reopen',
      // Shown beneath the disabled actions while the question still has pending
      // AI updates — they must be accepted or rejected before it can move on.
      blockedByPending: 'Resolve all AI updates first',
      confirmTitle: 'Mark as ready for review?',
      confirmBody:
        'This will flag the question as ready for review. Your team will be notified to sign off on this response.',
      confirmAction: 'Mark as ready',
      // The direct "mark as done" confirmation from the status pill — completing
      // a question WITHOUT going through review. Kept distinct from `approve` so
      // its copy doesn't imply a review/approval step it's actually bypassing.
      complete: {
        title: 'Mark question as done',
        body: 'This marks the question Done, bypassing review. It stays locked from AI updates and can be reopened later if needed.',
        action: 'Mark as done',
      },
      // The reviewer's sign-off confirmation, shown by the in-review banner.
      approve: {
        title: 'Approve question',
        body: 'This approves the draft and marks the question Done. It stays locked from AI updates and can be reopened later if needed.',
        action: 'Approve',
      },
      error: 'Could not update the status. Try again.',
    },
    // The reviewer's in-review banner and its request-changes (reject) dialog.
    // Shown when a question is flagged ready for review to a user who can change
    // its status; approving locks it (Done), requesting changes returns it to
    // in-progress with a mandatory reason (recorded as a rejection comment).
    responseReview: {
      bannerMessage:
        'This question is in review — approve it, or send it back with feedback.',
      requestChanges: 'Request changes',
      approve: 'Approve',
      reject: {
        title: 'Reject Draft',
        description:
          'What needs to change? Provide a clear description of what needs changing.',
        placeholder:
          "e.g. The C7.3 figure still doesn't include the fugitive-HFC line.",
        submit: 'Submit',
        error: 'Could not send the question back. Try again.',
      },
    },
    dataOwner: {
      label: 'Data owners',
      assign: 'Assign',
      unassigned: 'Unassigned',
      searchPlaceholder: 'Search people…',
      noPeople: 'No people found.',
      ownerCount_one: '{{count}} owner',
      ownerCount_other: '{{count}} owners',
      error: 'Could not update owners. Try again.',
    },
    question: {
      drawerLabel: 'Question details',
      // Muted suffix appended to the drawer's title (the active tab's name),
      // e.g. "Guidance — 7.30.18". Leading space separates it from the name.
      drawerCode: ' — {{code}}',
      drawerToggle: {
        show: 'Show details',
        hide: 'Hide details',
      },
      panes: {
        question: 'Question',
        editView: 'Edit view',
        inThisView: 'Panes in this view',
        questionAlwaysShown: 'Always shown',
        maxReached:
          'Up to {{count}} panes beside the question. Close one to add another.',
        swap: 'Change {{pane}} pane',
        swapLabel: 'Show in this pane',
        swapNone: 'Every pane is already open',
      },
      nav: {
        previous: 'Previous question',
        next: 'Next question',
        // The arrows' tooltips, naming the question each leads to.
        previousTo: 'Previous: {{code}}',
        nextTo: 'Next: {{code}}',
      },
      previousResponses: {
        priorLabel: '{{questionnaire}} · {{question}}',
        empty:
          'There are no previous responses to this question, or it was unanswered.',
        loading: 'Loading previous responses…',
        error: "Couldn't load previous responses. Try again in a moment.",
        comingSoonTables:
          'Previous responses for table questions are coming soon.',
        noAnswer: 'No prior answer recorded.',
        attachment: 'Attachment provided',
        changeType: {
          no_change: 'No change',
          minor_change: 'Minor change',
          modified_question: 'Modified question',
          modified_guidance: 'Modified guidance',
          additional_guidance: 'Additional guidance',
          revised_dependency: 'Revised dependency',
          unknown: 'Unknown',
        },
      },
      aiDraft: {
        draftWithAi: 'Draft with Annwn',
        drafting: 'Drafting…',
        composing: 'Drafting response…',
        working: 'Annwn is drafting',
        starting: 'Starting',
        clockLabel: '{{time}} so far, started {{at}}',
        stepsLabel: 'Drafting steps',
        latestStep: 'Latest activity: {{step}}',
        runLoadError:
          'Couldn’t load this draft’s progress. Annwn may still be working on it.',
        // Announced when a run finishes while the pane is open.
        outcome: {
          drafted: 'Annwn drafted a new answer.',
          no_change: 'Annwn found nothing to change.',
          declined: 'Annwn didn’t draft an answer.',
          failed: 'Annwn couldn’t finish the draft.',
        },
        startNew: 'Start a new draft',
        // The AI Draft tab: a collapsible run summary above the
        // pending AI updates awaiting review.
        summaryTitle: 'AI Draft Summary',
        updatesSectionLabel: 'AI updates',
        outcomeHeading: 'Outcome',
        draftedBody: 'Drafted a new answer.',
        dismissSummary: 'Dismiss summary',
        invite: {
          title: 'Let Annwn draft this answer',
          body: 'Annwn reads this workspace’s documents and suggests an answer. Nothing changes until you accept it.',
          bodyWithPrevious:
            'Annwn reads this workspace’s documents and last year’s responses, and suggests an answer. Nothing changes until you accept it.',
        },
        review: {
          title_one: 'Annwn drafted a change for you to review',
          title_other: 'Annwn drafted {{count}} changes for you to review',
          body_one:
            'Accept or reject it below. Once it’s reviewed, you can draft again.',
          body_other:
            'Accept or reject each one below. Once they’re reviewed, you can draft again.',
          bodyReviewOnly_one: 'Accept or reject it below.',
          bodyReviewOnly_other: 'Accept or reject each one below.',
        },
        unavailable: {
          in_review: {
            title: 'This answer is with a reviewer',
            body: 'It can’t be changed while it’s being reviewed.',
          },
          signed_off: {
            title: 'This answer is signed off',
            body: 'It can’t be changed unless it’s reopened.',
          },
          not_assigned: {
            title: 'This question isn’t assigned to you',
            body: 'You can change answers to the questions assigned to you.',
          },
          no_edit: {
            title: 'You can read this answer',
            body: 'Changing it needs permission to edit answers in this workspace.',
          },
        },
        options: {
          menuLabel: 'Draft options',
          usePreviousResponses: "Use last year's responses",
          toolEffortLabel: 'Tool usage',
          toolEffort: {
            low: 'Low',
            medium: 'Medium',
            high: 'High',
          },
          modelSizeLabel: 'Model tier',
          modelSize: {
            default: {
              label: 'Default',
              sublabel: 'The account’s drafting tier',
            },
            small: { label: 'Small', sublabel: 'Gemini Flash / Haiku / mini' },
            medium: {
              label: 'Medium',
              sublabel: 'Gemini Pro / Sonnet / GPT-5.4',
            },
            large: {
              label: 'Large',
              sublabel: 'Gemini Argon / Opus / GPT-5.5',
            },
            auto: {
              label: 'Auto',
              sublabel: 'Automatically uses the most suitable model',
            },
          },
        },
        error: 'Could not start the AI draft. Try again in a moment.',
        guidanceLabel: 'User guidance for the AI (optional)',
        guidancePlaceholder:
          'Steer the answer — e.g. focus on Scope 3, keep it under 200 words, cite the 2024 CTAP.',
        runGuidanceLabel: 'User guidance',
        startedAtLabel: 'Started {{at}}',
        confidenceLabel: 'Confidence',
        confidence: {
          high: 'High',
          medium: 'Medium',
          low: 'Low',
        },
        statusLabel: {
          queued: 'Queued',
          running: 'Running',
          drafted: 'Drafted',
          declined: 'Declined',
          no_change: 'No change needed',
          failed: 'Failed',
        },
        triggeredByUnknown: 'Unknown',
        viewTrace: 'View trace',
        srLabel: {
          finished: 'Finished',
          elapsed: 'Took',
          triggeredBy: 'Triggered by',
          model: 'Model',
          inputTokens: 'Input tokens',
          outputTokens: 'Output tokens',
          cost: 'Cost',
          attempt: 'Attempt',
          logfireTrace: 'Logfire trace',
          agentVersion: 'Drafter agent',
        },
        agentVersionValue: {
          stable: 'Stable',
          next: 'vNext',
        },
        runDetails: 'Run details',
        matrixStats: {
          newRows_one: '{{count}} new row added',
          newRows_other: '{{count}} new rows added',
          fieldsFilled_one: '{{count}} field filled',
          fieldsFilled_other: '{{count}} fields filled',
        },
        reasoningHeading: 'AI reasoning',
        declineBody: 'The drafter declined to answer this question.',
        // Per-decline-code copy; falls back to declineBody when the code is
        // absent or unrecognised. Keep in step with the DeclineReason union
        // (annwn_workflows.drafting.protocol).
        declineCode: {
          insufficientData: 'Insufficient data to draft a response.',
          ambiguousQuestion:
            'The question was too ambiguous to answer from the available data.',
          dataConflictsWithQuestion:
            'The available data conflicts with what the question asks.',
          outOfScope:
            'This question is outside the scope of the available data.',
          // Not the user's data failing a check — a post-LLM gate that discarded
          // a structurally invalid draft (wrong payload shape, ungrounded
          // citations, bad option codes). Reads as a drafting fault, not a
          // judgement about the question.
          validationFailed:
            'Something went wrong while drafting this answer, so it wasn’t saved. Try again in a moment.',
          tokenBudgetExceeded:
            'The drafter reached its processing limit before it could finish.',
          other: 'The drafter couldn’t draft a response for this question.',
        },
        noChangeBody:
          'The drafter reviewed the existing answer and found it already correct and current, so it left it unchanged.',
        failureBody:
          'The drafter could not complete this run. Try again in a moment.',
        // Per-failure-code copy; falls back to failureBody when the code is
        // absent or unrecognised. Keep in step with the FailureCode union.
        failureCode: {
          llmProviderError:
            'The AI service was temporarily unavailable. Try again in a moment.',
          persistError:
            'The draft was generated but could not be saved. Try again in a moment.',
          internalError:
            'The drafter hit an unexpected error. Try again in a moment.',
          fileTooLarge: 'The document was too large to process.',
          documentEmpty: 'The document appears to be empty.',
          documentUnreadable: 'The document could not be read.',
          unsupportedFileType: 'The file type is not supported.',
        },
      },
      aiUpdate: {
        badge_one: 'AI update',
        badge_other: 'AI updates',
        cardBadge: 'AI update',
        newRow: 'New row',
        reviewed: '{{reviewed}}/{{total}} updates reviewed',
        banner_one: '{{count}} AI update',
        banner_other: '{{count}} AI updates',
        bannerMatrix_one: '{{count}} AI field update across {{rows}} row',
        bannerMatrix_other: '{{count}} AI field updates across {{rows}} rows',
        accept: 'Accept',
        acceptAll: 'Accept all',
        rejectAll: 'Reject all',
        acceptError: 'Couldn’t accept this update. Try again.',
        acceptAllError: 'Couldn’t accept these updates. Try again.',
        reject: 'Reject',
        rejectDialog: {
          title: 'What could be better?',
          descriptionAll:
            'Your feedback applies to all {{count}} suggestions and helps improve future drafts.',
          reasons: {
            factuallyIncorrect: 'The answer is factually incorrect',
            wrongSource: 'The wrong source was used',
            incomplete: 'The answer is incomplete',
            outdated: 'The answer is outdated',
            lowConfidence: 'Confidence level is too low',
          },
          notePlaceholder: 'Add a note (optional)…',
          noteCount: '{{current}}/{{max}}',
          skip: 'Skip',
          submit: 'Submit feedback',
          error: 'Couldn’t reject. Try again.',
        },
      },
      activity: {
        confidence: '{{level}} confidence',
        previousValueLabel: 'Previous value',
        noPreviousValue: 'No previous value',
        suggestionLabel: 'AI suggestion',
        whyLabel: 'Why this suggestion',
        showMore: 'Show more',
        showLess: 'Show less',
        viewEvidence: 'View evidence',
        pageLabel: '{{index}} of {{total}}',
        prevUpdate: 'Previous update',
        nextUpdate: 'Next update',
        generatedAt: 'Generated {{at}}',
        olderDraft: 'Older draft',
        olderDraftHint:
          'A newer draft for this field was rejected, so this earlier AI suggestion is the one awaiting your review.',
        history: {
          title: 'Version history',
          draftedAnswer: 'drafted an answer',
          acceptedDraft: 'accepted the AI suggestion',
          rejectedDraft: 'rejected the AI suggestion',
          removedRow: 'removed the row “{{row}}”',
          removedRowUnnamed: 'removed a row',
          // Work-session header + collapse / batched reveal.
          madeChanges_one: 'made {{count}} change',
          madeChanges_other: 'made {{count}} changes',
          showAll_one: 'Show {{count}} change',
          showAll_other: 'Show all {{count}} changes',
          showMore_one: 'Show {{count}} more',
          showMore_other: 'Show {{count}} more',
          showLess: 'Show less',
          // Per-kind edit verbs (user edits).
          editedValue: 'changed the value',
          editedSelection: 'changed the selection',
          editedText: 'edited the field',
          // Diff renderers.
          emptyValue: 'Empty',
          removedOption: 'Removed option',
          added: 'Added',
          removed: 'Removed',
          addedCount_one: '{{count}} added',
          addedCount_other: '{{count}} added',
          removedCount_one: '{{count}} removed',
          removedCount_other: '{{count}} removed',
          showChanges: 'Show changes',
          hideChanges: 'Hide changes',
          readMore: 'Read more',
          readLess: 'Read less',
          agentName: 'Annwn',
          unknownUser: 'Someone',
          noPreviousValue: 'No previous value',
          cellLabel: '{{row}} · {{column}}',
          emptyTitle: 'No activity yet',
          emptyBody:
            'Each change to this answer will be listed here, with who made it and when.',
          loading: 'Loading version history…',
          error: 'Couldn’t load version history.',
        },
      },
      guidance: {
        segmentLabel: 'Guidance and drafting',
        tab: {
          guidance: 'Guidance',
          scoring: 'Scoring criteria',
          worked_examples: 'Worked examples',
          draft: 'AI Draft',
          previous: 'Previous responses',
          activity: 'Activity',
          comments: 'Comments',
        },
        empty: 'No guidance available for this question yet.',
        emptyScoring: 'No scoring criteria for this question yet.',
        emptyWorkedExamples: 'No worked examples for this question yet.',
        loading: 'Loading guidance…',
        error: 'Could not load guidance. Try again in a moment.',
        type: {
          simple_help: 'Help',
          popup_help: 'Help',
          info_block: 'Note',
          interpretation: 'Interpretation',
          scoring_criteria: 'Scoring criteria',
          worked_example: 'Worked example',
          common_error: 'Common errors',
          explanation_of_terms: 'Explanation of terms',
          ambition: 'Ambition',
          change_from_last_cycle: 'Change from last cycle',
        },
      },
      comments: {
        empty: 'No comments yet.',
        placeholder: 'Add a comment…',
        save: 'Save comment',
        saving: 'Saving…',
        loading: 'Loading comments…',
        error: 'Couldn’t load comments.',
        addError: 'Couldn’t save your comment. Try again.',
        delete: 'Delete comment',
        deleteConfirm: {
          title: 'Delete this comment?',
          description:
            'This removes the comment from the list. This can’t be undone from here.',
        },
        // The system-recorded review outcomes: an approval (green) and a
        // rejection (amber), the latter followed by the reviewer's reason.
        review: {
          approved: '{{name}} reviewed and approved the draft',
          rejected: '{{name}} reviewed and rejected the draft',
          note: 'Note',
        },
      },
      inputPlaceholder:
        'Editing for this question type ({{kind}}) is coming soon.',
      selectOneAriaLabel: 'Select an answer',
      selectOnePlaceholder: 'Select an answer…',
      selectManyAriaLabel: 'Select one or more answers',
      selectManyPlaceholder: 'Select answers…',
      selectManyMore_one: '+{{count}} more',
      selectManyMore_other: '+{{count}} more',
      selectFromGroups_one: 'All selections must come from one group.',
      selectFromGroups_other:
        'Selections must come from at most {{count}} groups.',
      selectUpTo_one: 'Select up to {{count}} answer.',
      selectUpTo_other: 'Select up to {{count}} answers.',
      selectAtLeast_one: 'Select at least {{count}} answer.',
      selectAtLeast_other: 'Select at least {{count}} answers.',
      selectBetween: 'Select between {{min}} and {{max}} answers.',
      selectExactly_one: 'Select exactly {{count}} answer.',
      selectExactly_other: 'Select exactly {{count}} answers.',
      searchPlaceholder: 'Search…',
      noOptionsMatch: 'No matches.',
      otherSpecifyLabel: 'Please specify',
      otherSpecifyPlaceholder: 'Enter your answer…',
      goToQuestion: 'Go to question',
      pickerSearchPlaceholder: 'Search by code or text…',
      richTextAriaLabel: 'Answer',
      richTextPlaceholder: 'Enter your answer…',
      richTextToolbar: {
        bold: 'Bold',
        italic: 'Italic',
        underline: 'Underline',
        bulletList: 'Bulleted list',
        orderedList: 'Numbered list',
      },
      charCount: '{{count}}/{{max}}',
      charCountError: 'Over the {{max}}-character limit — trim to save.',
      numericAriaLabel: 'Numeric value',
      numericPlaceholder: 'Enter a number…',
      numericRange: 'Between {{min}} and {{max}}',
      numericMin: 'At least {{min}}',
      numericMax: 'Up to {{max}}',
      numericExactly: 'Exactly {{value}}',
      numericDecimals_one: 'Up to {{count}} decimal place',
      numericDecimals_other: 'Up to {{count}} decimal places',
      dateAriaLabel: 'Pick a date',
      datePlaceholder: 'Select a date…',
      edit: {
        save: 'Save',
        saving: 'Saving…',
        undo: 'Undo changes',
        clear: 'Clear',
        clearing: 'Clearing…',
        error: 'Couldn’t save. Try again.',
      },
      matrixAriaLabel: 'Matrix answers',
      matrixRowFallback: 'Row {{n}}',
      dynamicMatrixEmpty: 'No rows yet. Add one to start answering.',
      dynamicMatrixAddRow: 'Add row',
      dynamicMatrixRemoveRow: 'Remove {{row}}',
    },
  },
  candidateGraph: {
    inspector: {
      kindCandidate: 'Candidate node',
      kindUnmapped: 'Unmapped candidate',
      kindCandidateRel: 'Candidate relationship',
      kindUnmappedRel: 'Unmapped relationship',
      noTypeLabel: '(no type)',
      propertiesHeading: 'Properties',
      noProperties: 'No properties',
      endpointsHeading: 'Endpoints',
      sourceLabel: 'Source',
      targetLabel: 'Target',
      citesHeading: 'Citations',
      noCites: 'No citations',
      citeGroupNode: 'Whole candidate',
      selectPrompt: 'Select a node or edge to inspect',
      reconciliationHeading: 'Reconciliation',
      relStatus: {
        created: 'created',
        confirmed: 'confirmed',
        conflicts: 'conflicts',
        pending: 'pending',
      },
    },
    rail: {
      searchPlaceholder: 'Search nodes',
      legendLabel: 'Dot colours',
      unmappedGroup: 'Unmapped',
      noTypeGroup: '(no type)',
      showMore: 'Show {{count}} more',
      noMatches: 'No nodes match',
      selectPrompt: 'Select a node from the list to explore its neighbourhood',
      outcome: {
        disputed: 'in dispute',
        conflicts: 'conflicts',
        resolved: 'resolved',
        confirmed: 'confirmed',
        created: 'created',
        unreconciled: 'not reconciled',
      },
    },
    header: {
      backToDocuments: 'Back to documents',
      candidatesHeading: 'Extracted candidates',
      countsLabel: '{{nodes}} nodes · {{edges}} edges',
      openFullView: 'Open full view',
    },
    states: {
      notExtracted: 'Extraction has not run for this file yet.',
      extracting: 'Extraction is in progress.',
      failed: 'Extraction failed: {{reason}}',
      // Friendly reason phrases interpolated into `failed`, keyed by the
      // server's FailureCode. internalError doubles as the fallback when the
      // code is absent or unrecognised.
      failureReason: {
        llmProviderError: 'the AI service was temporarily unavailable',
        persistError: 'the results could not be saved',
        internalError: 'an unexpected error occurred',
      },
      oversized: 'File too large to extract in v1.',
      emptyInput: 'There was no text in this file to extract from.',
      empty: 'Extraction succeeded but produced no candidates.',
      loading: 'Loading graph…',
      nothingToDraw: 'No graph yet for this document',
      loadError: {
        title: 'Couldn’t load this graph',
        body: 'Something went wrong fetching the graph. Try again in a moment.',
      },
    },
    canonical: {
      edgeLabel: {
        created: 'created',
        confirmed: 'confirmed',
        conflict: 'conflict',
        potentialMatch: 'potential match',
        resolvedFrom: 'resolved-from',
        resolvedTo: 'resolved-to',
      },
      createdTooltip:
        'Nothing existing matched this candidate, so reconciliation created a new canonical node from it.',
      confirmedTooltip:
        'This candidate matched an existing canonical, and its values were written onto it.',
      conflictTooltip:
        'This candidate matched an existing canonical, but some values disagree — the canonical was left untouched, pending review.',
      resolvedFromTooltip:
        "This candidate relationship's source endpoint resolved to this canonical.",
      resolvedToTooltip:
        "This candidate relationship's target endpoint resolved to this canonical.",
      potentialMatchTooltip:
        'This candidate likely matches the canonical, but some values disagree — pending human review.',
      pendingChip: 'Pending',
      kindCanonical: 'Canonical node',
      reconciledFromHeading: 'Reconciled from',
      relationshipMatches_one: '{{count}} relationship resolved here',
      relationshipMatches_other: '{{count}} relationships resolved here',
      noInboundCandidates:
        'No candidates from this file reconciled to this canonical.',
    },
    reconciliationEdgeInspector: {
      kindLabel: 'Reconciliation edge',
      endpointsHeading: 'Endpoints',
      sourceLabel: 'Source',
      targetLabel: 'Target',
      metadataHeading: 'Metadata',
      noMetadata: 'No properties',
      disputedFieldsHeading: 'Proposed changes (pending review)',
      overwrittenFieldsHeading: 'Overwritten values',
      upsertedFields: 'Upserted fields',
      conflictFields: 'Conflict fields',
      resolutionNotes: 'Resolution notes',
      createdBy: 'Created by',
      createdDate: 'Created date',
      confirmedBy: 'Confirmed by',
      confirmedDate: 'Confirmed date',
      reviewedBy: 'Reviewed by',
      reviewedDate: 'Reviewed date',
      reviewStatus: 'Review status',
      reconciledBy: 'Reconciled by',
      reconciledDate: 'Reconciled date',
    },
    canonicalEdgeInspector: {
      kindLabel: 'Canonical edge',
      endpointsHeading: 'Endpoints',
      sourceLabel: 'Source',
      targetLabel: 'Target',
      propertiesHeading: 'Properties',
      noProperties: 'No properties',
    },
    cite: {
      pagedBlock: 'Page {{page}}, block {{block}}',
      pagedCell:
        'Page {{page}}, table block {{block}}, row {{row}}, column {{col}}',
      pagedSpan: 'Page {{page}}, blocks {{start}}–{{end}}',
      sheetCell: '{{sheet}}, block {{block}}, row {{row}}, column {{col}}',
    },
    evidenceViewer: {
      title: 'Evidence',
      loading: 'Loading evidence…',
      empty: 'No evidence found for this version.',
      prevCitation: 'Previous citation',
      nextCitation: 'Next citation',
      pageLabel: 'Page {{page}}',
      priorAnswerLabel: 'Our answer to {{code}}',
      priorAnswerLabelUnknown: 'Our earlier answer',
      factCount_one: '{{count}} fact',
      factCount_other: '{{count}} facts',
      showMore: 'Show {{count}} more',
      // Returns from a finding's evidence to the finding's sources.
      back: 'Back to sources',
      byFact: 'Facts',
      byDocument: 'Document',
      // A sheet's cited rows, against all of it.
      rowsUsed_one: '{{count}} of {{total}} rows used',
      rowsUsed_other: '{{count}} of {{total}} rows used',
      // A citation the source matched to its sheet but to no one row of it.
      rowNotPinned: 'No single row could be pinned for this citation.',
      // A row found by matching its fact's figure, month and labels to the
      // row's cells, the source having pinned it to none.
      // What a record went into: an answer's findings, by label.
      fedInto: 'Went into {{findings}}',
      // What the marks down a sheet's edge mean.
      legend: {
        used: 'Used',
        notUsed: 'Not used',
      },
      onlyUsedRows: 'Only used rows',
      rowNumber: 'Row',
      highlights: 'Highlights',
      download: 'Download original',
      cannotShow: 'This kind of file can’t be shown here.',
      couldNotRead: 'The file couldn’t be read here.',
      tooLarge: 'The file is too large to show here.',
      // Cited passages the document as read doesn't hold where they're found.
      passagesNotFound_one:
        'This wasn’t found in the document as it reads now, so it’s shown as cited.',
      passagesNotFound_other:
        'These weren’t found in the document as it reads now, so they’re shown as cited.',
      // After any of the three, where something of the file was cited.
      usedBelow: 'What was used of it is below.',
      sheetNotUsed: 'Nothing on this sheet was used.',
      // A sheet whose cited rows weren't found in the file as read.
      notPlaced:
        'The rows used couldn’t be found in the file as it reads now, so they’re shown as cited.',
      // The same, where the sheet was cut short to what the browser reads.
      notPlacedPastPreview:
        'The rows used aren’t in the part of the sheet shown here, so they’re shown as cited.',
      // The question an answer's evidence is for, after "Evidence ·".
      quoted: '“{{text}}”',
      // Follows "Evidence" in the viewer's title: what the evidence is for.
      titleSubject: '· {{subject}}',
      viewLabel: 'View',
      filesAriaLabel: 'Files',
      factsAriaLabel: 'Facts',
      noteLabel: 'Why this was used',
      // What an ExpressionValue fact is called beside its reason.
      figureKind: 'Figure',
      passageLabel: 'Passage',
      // The rail's groups: the files the answer drew on, those the source
      // couldn't read, and the account's own answers it carried forward.
      rail: {
        direct: 'Relied on',
        supporting: 'Supporting',
        unread: 'Not read',
        prior: 'Earlier answers',
      },
      // A sheet none of whose rows the answer's citations could be located in.
      noRowLocated_one: '{{count}} row, none located',
      noRowLocated_other: '{{count}} rows, none located',
      railPages_one: 'p. {{pages}}',
      railPages_other: 'pp. {{pages}}',
      passages_one: '{{count}} passage',
      passages_other: '{{count}} passages',
      pagesUsed_one: '{{count}} of {{total}} pages used',
      pagesUsed_other: '{{count}} of {{total}} pages used',
      // A run of a sheet's rows the answer didn't use, folded into one line.
      rowsNotUsed_one: '{{count}} row not used',
      rowsNotUsed_other: '{{count}} rows not used',
      // Where the reader is in the file in hand.
      counter: {
        row: 'Row {{current}} of {{total}} in this file',
        passage: 'Passage {{current}} of {{total}} in this file',
        citation: 'Citation {{current}} of {{total}} in this file',
      },
      // A fact cited in more than one place, in the by-fact list.
      factPlaces_one: '{{count}} place',
      factPlaces_other: '{{count}} places',
      keyHint:
        '<k>j</k> <k>k</k> next or previous · <k>[</k> <k>]</k> file · <k>Esc</k> back',
      unreadTitle: 'This file couldn’t be read',
      // Why the source couldn't place a citation, by its code.
      unreadReasons: {
        bad_cite: 'The citation didn’t name a place in the file.',
        block_not_found: 'The cited part of the file wasn’t found.',
        parse_run_missing: 'The file hasn’t been read yet.',
        parse_read_failed: 'Reading the file failed.',
        other: 'The source couldn’t place this citation.',
      },
      close: 'Close',
    },
    cypher: {
      copyTooltip: 'Copy Cypher',
      uuidLabel: 'uuid',
    },
    focus: {
      focusHere: 'Centre the graph here',
      expand: 'Expand',
      collapse: 'Collapse',
      escapeHint: 'Press Escape to leave the expanded view',
      neighbourCount_one: '{{count}} neighbour',
      neighbourCount_other: '{{count}} neighbours',
      omittedNeighbours_one: '{{count}} neighbour not shown',
      omittedNeighbours_other: '{{count}} neighbours not shown',
      orphanNotice: 'This node has no relationships',
      backToOverview: 'Back to overview',
    },
    overview: {
      title: 'Overview',
      focusTab: 'Explore',
      viewToggleLabel: 'View',
      colourBy: 'Colour by',
      dimension: {
        type: 'Type',
        outcome: 'Reconciliation',
        kind: 'Unmapped',
      },
      kind: { mapped: 'Mapped', unmapped: 'Unmapped' },
      legendLabel: 'Legend',
      orphanGutter: 'Unconnected',
      layingOut: 'Annwntangling…',
      nodeCount_one: '{{count}} node',
      nodeCount_other: '{{count}} nodes',
      hint: 'Click a node to explore its neighbourhood',
      expand: 'Expand',
      collapse: 'Collapse',
      escapeHint: 'Press Escape to leave the expanded view',
    },
  },
  graphSearch: {
    title: 'Graph search',
    navLink: 'Graph search',
    searchPlaceholder: 'Search the knowledge graph…',
    searchAriaLabel: 'Search the knowledge graph',
    minCharsHint: 'Type at least {{n}} characters to search.',
    typeFilter: {
      triggerLabel: 'Node types',
      allTypes: 'All node types',
      searchPlaceholder: 'Filter types…',
      empty: 'No matching types.',
      selectedCount: '{{n}} selected',
    },
    debug: {
      toggleLabel: 'Debug mode',
      matchedIn: 'Matched in',
      vector: 'vector',
      fulltext: 'fulltext',
      rank: 'rank',
      score: 'score',
      rrf: 'RRF term',
      embeddingText: 'Embedded text',
      templateVersion: 'Template version',
      degradedFulltextEmpty:
        'Degraded: no fulltext matches — vector-only results',
      degradedVectorEmpty:
        'Degraded: no vector results — fulltext-only results',
      fulltextQuery: 'Fulltext query',
      candidates: '{{vector}} vector / {{fulltext}} fulltext candidates',
      latency:
        'embed {{embed}}ms · vector {{vector}}ms · fulltext {{fulltext}}ms',
    },
    view: {
      list: 'List',
      graph: 'Graph',
    },
    results: {
      empty: 'No matching nodes.',
      loading: 'Searching…',
      error: 'Search failed.',
      scoreLabel: 'score',
      propertiesHeading: 'Properties',
      noProperties: 'No properties.',
    },
    graph: {
      neighboursHeading: 'Neighbours',
      expandAll: 'Expand all',
      expanding: 'Expanding…',
      noNeighbours: 'No neighbours.',
      relTypeCount: '{{relType}} ({{direction}}) × {{count}}',
    },
    inspector: {
      typeLabel: 'Type',
      relTypeLabel: 'Relationship',
    },
  },
  internal: {
    noAccess: 'You do not have access to this internal tool.',
  },
  ontology: {
    // Curated display copy for graph vocabulary, keyed by the backend's RAW
    // node labels / relationship types — those arrive untranslated, so
    // translatability is bounded: these curate the rendering of known values,
    // and unmapped ones fall back to the algorithmic humanisers in
    // src/lib/ontology.ts. Display-only — never key data paths on these.
    nodeLabels: {
      MetricObservation: 'Observation',
      // A cited answer of the account's own arrives spelled two ways: source
      // cards get the BFF's display kind (`QuestionnaireAnswer`), evidence
      // locations still carry the raw ontology label. Same wording either way.
      QuestionnaireAnswer: 'Previous questionnaire response',
      QuestionnaireQuestionResponseVersion: 'Previous questionnaire response',
      QuestionnaireQuestionResponse: 'Previous questionnaire response',
      PersonRole: 'Person/role',
    },
    // The wording that joins a cited connection's two ends into a sentence
    // ("Chair *is a member of* Board of Directors") where the generic
    // lowercasing would miss its copula or article. Verb-led types (SUPPLIES,
    // REPORTS_TO, …) read fine via the fallback and stay unlisted. Covers the
    // citable domain edges of both ontology versions (v1 accounts keep their
    // graphs, so v1-only types stay).
    relationshipConnectives: {
      MEMBER_OF: 'is a member of',
      PART_OF: 'is part of',
      COMPOSED_OF: 'is composed of',
      HAS_COMPONENT: 'includes',
      HOLDS_ROLE: 'holds the role of',
      AFFILIATED_WITH: 'is affiliated with',
      INVOLVED_IN: 'is involved in',
      LOCATED_AT: 'is located at',
      LOCATED_IN: 'is located in',
      IN_BOUNDARY: 'is within the boundary of',
      RESPONSIBLE_FOR: 'is responsible for',
      ACCOUNTABLE_FOR: 'is accountable for',
      GOVERNED_BY: 'is governed by',
      INFORMED_BY: 'is informed by',
      RESOLVED_BY: 'is resolved by',
      DERIVED_FROM: 'is derived from',
      EXTRACTED_FROM: 'was extracted from',
      ASSEMBLES_FROM: 'is assembled from',
      IS_VERSION_OF: 'is a version of',
      OPERATES_OWNS: 'operates or owns',
      MAKES_APPROVES: 'makes or approves',
      CORROBORATES_CONTRADICTS: 'corroborates or contradicts',
    },
    title: 'Ontology',
    versionAriaLabel: 'Ontology version',
    filterPlaceholder: 'Filter types…',
    fullPrompt: 'Full prompt',
    nodeTypes: 'Node types',
    relationships: 'Relationships',
    usages: 'Usages',
    outgoing: 'Outgoing',
    incoming: 'Incoming',
    typeNotFound: 'No node type with this name in this version.',
    relationshipNotFound:
      'No relationship type with this name in this version.',
    empty: 'This ontology version defines no node types.',
    llmHintCaption: 'LLM hint — sent to the model',
    descriptionCaption: 'Description — documentation only',
    hintTag: 'LLM hint',
    notesTag: 'Human hint',
    exampleTag: 'Example',
    llmSkipTag: 'Withheld from the model',
    agentWord: '{{value}} → {{words}}',
    agentName: {
      typeCaption: 'Agent name — what agents are told a record of it is',
      relationshipCaption:
        'Agent wording — how agents are told it reads, start to end',
      kindFrom: 'Depends on its <code>{{property}}</code>:',
      kindFallback:
        'A record without a <code>{{property}}</code> value is shown as “{{name}}”.',
      asFigure: 'Headed by the figure it holds rather than by this name.',
      standingFrom: 'Its standing, from its <code>{{property}}</code>:',
    },
    inheritedFrom: 'from {{base}}',
    inheritance: 'Inheritance',
    graph: {
      nodeMeta_one: '{{count}} property',
      nodeMeta_other: '{{count}} properties',
      relMeta_one: '{{count}} relationship',
      relMeta_other: '{{count}} relationships',
      nothingSelected:
        'Select a type or a relationship to see what it is and jump to its page.',
      openPage: 'Open full page',
      exploreFrom: 'Explore {{type}}',
      expand: 'Expand',
      collapse: 'Collapse',
      escapeHint: 'Press Esc to collapse',
      empty: 'Nothing connects to this in the visible layer.',
      egoCaption:
        'Everything one hop from {{type}} — what points at it on the left, what it points at on the right. Only edges touching {{type}} are drawn.',
      relCaption: 'The endpoint pairs {{rel}} is declared for.',
    },
    contributesProperties_one: 'contributes {{count}} property',
    contributesProperties_other: 'contributes {{count}} properties',
    embeddingTemplate: 'Embedding template',
    embeddingTemplateCaption:
      'The text built for each node of this type and embedded for semantic search — placeholders are filled from the node’s own properties.',
    tabs: {
      properties: 'Properties',
      relationships: 'Relationships',
      graph: 'Graph',
      llm: 'LLM view',
    },
    badges: {
      indexed: 'indexed',
      skipDedup: 'skip dedup',
      uuidIndexOnly: 'uuid index only',
      extends: 'extends {{base}}',
      requiresOut: 'requires {{rel}} →',
      requiresIn: 'requires ← {{rel}}',
    },
    properties: {
      property: 'Property',
      type: 'Type',
      guidance: 'Guidance',
      none: 'No properties.',
      showInternal: 'Show internal properties ({{n}})',
      hideInternal: 'Hide internal properties',
    },
    llm: {
      variants: {
        extractionEnterprise: 'Extraction (hints on)',
        drafter: 'Drafter · Ask Annwn (names-only)',
      },
      pretty: 'Pretty',
      raw: 'Raw',
      schemaGuidance: 'Schema guidance',
      schemaGuidanceCaption:
        'Applies to the whole schema rather than any one type — rendered under the “Node Types” header.',
      schemaGuidanceAbsent:
        'This variant carries no schema-level guidance: names-only renderings drop every hint, so the drafter and Ask Annwn never receive it.',
      sharedHints: 'Shared property guidance',
      sharedHintsCaption:
        'Hoisted out of the per-type lines because the same hint sits on this property across several types — the type lines then omit it.',
      notPresent:
        'Not present in this variant — the model never sees this type here.',
      viewFullPrompt: 'View in full prompt',
      promptMeta: '{{chars}} characters',
      keptDropped:
        'Hints and enum values survive distillation; descriptions, usage examples and property notes are never sent to the model.',
      drafterWrapNote:
        'The drafter and Ask Annwn inject this with the header lines replaced by “# Evidence types”, wrapped in an <evidence_schema> block with a short searchability preamble — the type and relationship lines themselves are byte-identical.',
    },
  },
  // The standalone eval viewer (eval-viewer.html), an internal
  // tool for reading annwn-platform's gold cases and seed graph worlds.
  evalViewer: {
    title: 'Eval viewer',
    dropPrompt:
      'Drop an extraction eval case or a seed graph world (.yaml) here, or click to choose one',
    openAnother: 'Open another file',
    parseError:
      "Couldn't read {{fileName}} as an extraction eval case or a seed graph world",
    summary:
      '{{fileName}} · {{documentType}} · {{nodes}} nodes · {{relationships}} relationships expected',
    expectsNothing: 'This case expects nothing to be extracted.',
    labelFromRef: '— (no name expected; labelled by its ref)',
    world: {
      summary:
        '{{fileName}} · seed graph world · {{nodes}} nodes · {{relationships}} relationships',
      empty: 'This world has no nodes.',
      labelFromRef: '— (no name; labelled by its ref)',
    },
    budget: {
      additionalNodes_one: 'up to {{count}} additional node',
      additionalNodes_other: 'up to {{count}} additional nodes',
      additionalRelationships_one: 'up to {{count}} additional relationship',
      additionalRelationships_other: 'up to {{count}} additional relationships',
      unmappedMaximum_one: 'up to {{count}} unmapped node',
      unmappedMaximum_other: 'up to {{count}} unmapped nodes',
      unmappedPercent: 'up to {{percent}} unmapped nodes',
    },
    matcher: {
      equals: '= {{value}}',
      matches: '≈ {{value}}',
      includes: 'includes {{values}}',
      after: 'after {{date}}',
      before: 'before {{date}}',
      approx: '≈ {{value}} (± {{tolerance}})',
      exactly: 'exactly [{{values}}]',
      absent: 'must be unset',
      anyOf: 'any of: {{alternatives}}',
      null: 'null',
      andSeparator: ' and ',
      orSeparator: ' | ',
    },
  },
  // Shared by every surface that shows the platform's cited working — the
  // questionnaire's AI-update card and Ask Annwn's evidence column.
  provenance: {
    sourcesLabel: 'Sources',
    rankedByRelevance: 'ranked by relevance',
    openSource: 'Open source {{label}}',
    // Shown on a reference chip when the upload records no filename. The chip
    // still opens — the citation points at the upload by uuid, which is what
    // resolving a highlight needs.
    untitledDocument: 'Untitled document',
    // The run of places a document was cited, under its name on the chip. The
    // list itself is joined client-side; only the leading word is translated.
    pageLabel_one: 'Page {{pages}}',
    pageLabel_other: 'Pages {{pages}}',
    sheetLabel: 'Sheet {{sheets}}',
    // What an answer drew on, file by file.
    files: {
      // A file opens in the evidence viewer; its icon downloads the file as
      // uploaded.
      openIn: 'Open {{filename}} in the evidence',
      download: 'Download {{filename}}',
      oneByOne: 'Every fact, one by one',
    },
    moreSources_one: 'Show {{count}} more source',
    moreSources_other: 'Show {{count}} more sources',
    fewerSources: 'Show fewer sources',
  },
  askAnnwn: {
    navLink: 'Ask Annwn',
    noAccess: 'You do not have access to Ask Annwn.',
    // The panel beside the conversation that lists the reader's own threads.
    threads: {
      label: 'Threads',
      newQuestion: 'New question',
      toggle: 'Toggle threads',
      search: 'Search threads',
      today: 'Today',
      earlier: 'Earlier',
      empty: 'Your questions will appear here.',
      noMatches: 'No threads match your search.',
      untitled: 'Untitled question',
      clearSearch: 'Clear search',
      matches_one: '{{count}} of {{total}} threads matches',
      matches_other: '{{count}} of {{total}} threads match',
      // Under a thread the search found in its messages rather than its name.
      inMessage: 'In a message',
      inMessageAt: 'In a message · {{time}}',
      options: 'Options for {{name}}',
      rename: 'Rename',
      renameTitle: 'Rename thread',
      nameLabel: 'Name',
    },
    title: 'Ask Annwn about your data.',
    // Above each of the reader's questions in a thread.
    message: {
      youAsked: 'You asked',
      youAskedAt: 'You asked · {{time}}',
      followUp: 'Follow-up',
      followUpAt: 'Follow-up · {{time}}',
    },
    composer: {
      label: 'Your question',
      placeholder: 'Write message…',
      send: 'Ask Annwn',
      // Deliberately short: the callout above it carries the explanation, so
      // repeating "nothing carries over" here just says it twice on one screen.
      closed: 'This conversation is full. Start a new chat to carry on.',
      sendFailed: 'Your message could not be sent. Try again.',
    },
    // The question field a thread starts from, on the start page and the
    // dashboard.
    field: {
      placeholder: 'Ask Annwn about your data…',
      ask: 'Ask',
    },
    // The dashboard's question field, with its suggestions.
    bar: {
      label: 'Ask Annwn',
    },
    starters: {
      label: 'Suggested questions',
      ask: 'Ask',
      // Each question with the one line that says what the answer will hold.
      questions: {
        energyTotal: {
          question: 'What was our energy consumption in 2025?',
          detail: 'Electricity, gas, steam and fleet fuel in MWh, by site',
        },
        energySites: {
          question:
            'Which sites use the most energy, and where is data missing?',
          detail: 'Sites ranked, with estimated and missing months flagged',
        },
        energyRenewable: {
          question: 'How much of our electricity is on a renewable tariff?',
          detail: 'Share of MWh by supply contract, site by site',
        },
        travelSources: {
          question: 'Where do our business travel emissions come from?',
          detail: 'Flights, rail, hotels and car hire in tCO₂e',
        },
        travelLongHaul: {
          question: 'Which offices book the most long-haul business class?',
          detail: 'Trips, distance and emissions by office',
        },
      },
    },
    clarification: {
      freeText: 'Something else',
      dismiss: 'Dismiss this question',
      // Heads the options the reader can answer with.
      options: 'Options',
      // While the question can be answered: the status, then the step it
      // paused on as the progress list names it.
      waitingAtStep: '<status>Waiting for your answer</status> · {{step}}',
    },
    progress: {
      // Still queued after a few minutes: still being checked on.
      slowToStart:
        'Annwn is busy with other work and hasn’t started on this yet. It will, and the answer will appear here.',
      // Still queued after the longest wait: no longer being checked on.
      stillQueued: 'Annwn still hasn’t started on this one.',
      // Running with no progress for too long: no longer being checked on.
      stalled: 'Annwn is taking longer than expected on this one.',
      pollFailed: 'Annwn’s progress on this one couldn’t be checked.',
      checkAgain: 'Check again',
    },
    // The steps of a planned reply. Only the agents a reader can follow get a
    // row; the planner never shows, and any other agent reads as the
    // generic `other` row.
    plan: {
      label: 'Progress',
      // Heads the working panel from the moment a worker has the reply to its
      // answer, whatever the plan holds.
      thinking: 'Annwn is working',
      // Heads the panel while the reply is queued, before any work has started.
      waiting: 'Waiting for Annwn to start',
      waitingLine: 'Your question is in the queue.',
      waitingHint:
        'Annwn will start on it as soon as it’s free. You can leave this page; the answer will be here when you come back.',
      // The running agent's result line, and a finished one's.
      working: 'Working…',
      found_one: '{{count}} found',
      found_other: '{{count}} found',
      // The findings a step wrote, and the figures it worked out: each a part
      // of a finished step's line and one of the totals.
      metrics_one: '{{count}} metric',
      metrics_other: '{{count}} metrics',
      calculations_one: '{{count}} calculation',
      calculations_other: '{{count}} calculations',
      // Between the parts of a finished step's line: what it found, wrote and
      // worked out, then how long it took.
      resultParts: '{{before}} · {{after}}',
      // The running step's latest finding, under its label as the agent wrote it.
      metric: 'Metric: {{label}}',
      // The running step's latest calculation: how it worked its figures out,
      // the measure the first belongs to and how many more there are, and the
      // result. `measure` is a name from the customer's data, shown as
      // written; `value` is the result with its unit.
      calc: {
        add: {
          withoutMeasure_one: 'Added {{count}} figure: {{value}}',
          withoutMeasure_other: 'Added {{count}} figures: {{value}}',
          withMeasure_one: 'Added {{count}} figure, {{measure}}: {{value}}',
          withMeasure_other: 'Added {{count}} figures, {{measure}}: {{value}}',
          withMeasures_one:
            'Added {{count}} figure, {{measure}} and {{more}} more: {{value}}',
          withMeasures_other:
            'Added {{count}} figures, {{measure}} and {{more}} more: {{value}}',
        },
        subtract: {
          withoutMeasure: 'Took one figure from another: {{value}}',
          withMeasure: 'Took one figure from another, {{measure}}: {{value}}',
          withMeasures:
            'Took one figure from another, {{measure}} and {{more}} more: {{value}}',
        },
        multiply: {
          withoutMeasure_one: 'Multiplied {{count}} figure: {{value}}',
          withoutMeasure_other: 'Multiplied {{count}} figures: {{value}}',
          withMeasure_one:
            'Multiplied {{count}} figure, {{measure}}: {{value}}',
          withMeasure_other:
            'Multiplied {{count}} figures, {{measure}}: {{value}}',
          withMeasures_one:
            'Multiplied {{count}} figure, {{measure}} and {{more}} more: {{value}}',
          withMeasures_other:
            'Multiplied {{count}} figures, {{measure}} and {{more}} more: {{value}}',
        },
        divide: {
          withoutMeasure: 'Divided one figure by another: {{value}}',
          withMeasure: 'Divided one figure by another, {{measure}}: {{value}}',
          withMeasures:
            'Divided one figure by another, {{measure}} and {{more}} more: {{value}}',
        },
        percentage_of: {
          withoutMeasure: 'Took one figure as a share of another: {{value}}',
          withMeasure:
            'Took one figure as a share of another, {{measure}}: {{value}}',
          withMeasures:
            'Took one figure as a share of another, {{measure}} and {{more}} more: {{value}}',
        },
        percentage_change: {
          withoutMeasure:
            'Took the change from one figure to another: {{value}}',
          withMeasure:
            'Took the change from one figure to another, {{measure}}: {{value}}',
          withMeasures:
            'Took the change from one figure to another, {{measure}} and {{more}} more: {{value}}',
        },
        convert: {
          withoutMeasure: 'Converted a figure to another unit: {{value}}',
          withMeasure:
            'Converted a figure to another unit, {{measure}}: {{value}}',
          withMeasures:
            'Converted a figure to another unit, {{measure}} and {{more}} more: {{value}}',
        },
      },
      foundSoFar: 'Found so far',
      results_one: '{{count}} result',
      results_other: '{{count}} results',
      searches_one: '{{count}} search',
      searches_other: '{{count}} searches',
      // Short stage names in the working panel, beside each step's phrasing.
      agentNames: {
        planner: 'Plan',
        classify: 'Classify',
        shape: 'Shape',
        curate: 'Curate',
        analyse: 'Analyse',
        author: 'Author',
        review: 'Review',
        other: 'Working',
      },
      // Live: the running step cycles through its list every few seconds (the
      // first one only, under reduced motion); every other row shows the first.
      phrases: {
        // The planner, before any agent has started: the panel's first row.
        planner: [
          'Reading your question…',
          'Working out how to answer it…',
          'Choosing which agents to ask…',
          'Planning the steps…',
        ],
        classify: [
          'Working out what you’re asking about…',
          'Reading the question twice…',
          'Spotting the topics behind it…',
          'Checking it’s one Annwn knows…',
          'Sizing up the question…',
          'Picking out what matters…',
        ],
        shape: [
          'Pinning down exactly what you need…',
          'Settling the period and sites…',
          'Checking which sites are in scope…',
          'Reading what’s on file about you…',
          'Agreeing the units before the numbers…',
          'Listing the data the answer needs…',
        ],
        curate: [
          'Searching your sustainability data…',
          'Leafing through the annual reports…',
          'Following the paper trail…',
          'Checking behind every meter…',
          'Turning over the spreadsheets…',
          'Asking the archive nicely…',
          'Opening a few more folders…',
          'Cross-referencing the invoices…',
          'Dusting off last year’s numbers…',
        ],
        analyse: [
          'Checking and calculating…',
          'Double-checking the sums…',
          'Running the numbers…',
          'Making sure the kWh and MWh are on speaking terms…',
          'Squaring the totals with the parts…',
          'Doing the maths so you don’t have to…',
          'Carrying the one…',
          'Checking the periods line up…',
          'Sanity-checking the big numbers…',
        ],
        author: [
          'Writing it up…',
          'Finding the clearest way to say it…',
          'Tidying the tables…',
          'Choosing words an auditor would approve of…',
          'Trimming the waffle…',
          'Adding the finishing touches…',
          'Putting the headline first…',
          'Rounding off the edges…',
          'Making it easy to skim…',
        ],
        review: [
          'Checking the answer before you see it…',
          'Tracing the figures back to the records…',
          'Re-doing the sums…',
          'Making sure nothing’s missing…',
          'Checking every number has a source…',
          'Reading it as an auditor would…',
        ],
        other: ['Working on it…', 'Still on it…', 'Nearly there…'],
      },
      // What each agent does, where a reply names the step it stopped on.
      agents: {
        classify: 'Working out what you’re asking about',
        shape: 'Pinning down exactly what you need',
        curate: 'Searching your sustainability data',
        analyse: 'Checking and calculating',
        author: 'Writing it up',
        review: 'Checking the answer',
        other: 'Working on it',
      },
      // Each status is an icon, so each carries its name for screen readers.
      status: {
        pending: 'Not started',
        running: 'In progress',
        completed: 'Done',
        failed: 'Failed',
        blocked: 'Waiting for your answer',
      },
      // Announced, not shown: the running step and its latest search, metric
      // or calculation.
      announcement: '{{step}} {{line}}',
    },
    copyAnswer: 'Copy answer',
    jumpToLatest: 'Latest',
    runDetails: {
      // Internal-admin only. Deliberately vague rather than "Debug" or "Run
      // details": the toggle sits beside the sources link in a customer-facing
      // thread, so it reads as an unremarkable disclosure to anyone who does
      // somehow see it.
      toggle: 'More information',
      viewTrace: 'View trace',
      // Phrased per value because the wording capitalises it — "Medium tier",
      // not "medium tier". A tier with no entry here renders raw, via the
      // caller's `defaultValue`.
      //
      // "tier" rather than "model": these name the size band the run picked, not
      // the model itself, which each agent's own chip names.
      modelSize: {
        small: 'Small tier',
        medium: 'Medium tier',
        large: 'Large tier',
      },
      toolEffort: {
        low: 'Low effort',
        medium: 'Medium effort',
        high: 'High effort',
      },
      thinkingLevel: {
        low: 'Low thinking',
        medium: 'Medium thinking',
        high: 'High thinking',
      },
      // Icon-only chips, so each carries its field name for screen readers.
      srLabel: {
        finished: 'Finished',
        elapsed: 'Elapsed',
        modelSize: 'Model tier',
        toolEffort: 'Tool effort',
        thinkingLevel: 'Thinking',
        logfireTrace: 'Logfire trace',
        model: 'Model',
        agents: 'Agents',
      },
      // Short agent names for a model pill's tooltip, keyed by the agent's id. An
      // agent with no entry takes its account-settings name.
      agents: {
        'chat:responder': 'Answer',
        'chat:planner': 'Planning',
        'concepts:classifier': 'Concepts',
        'agent:shape': 'Shape',
        'agent:curate': 'Curate',
        'agent:analyse': 'Analyse',
        'agent:author': 'Author',
        'agent:review': 'Review',
      },
    },
    sources: {
      // The evidence column's Sources tab when the reply cited nothing; the list
      // itself is rendered by the shared SourceList, which brings its own copy.
      empty: 'No sources were recorded for this answer.',
      // A declined reply that looked and found nothing it could use; on file
      // when what it lacked was data the reader could upload.
      nothingSuitable: 'Nothing suitable was found in your vault.',
      nothingOnFile:
        'Nothing suitable was found in your vault. Upload a document to answer this.',
    },
    // The bar charts the display agent can place in an answer.
    chart: {
      // Names the chart for screen readers when the agent gave it no title.
      untitled: 'Chart',
      summary_one: '{{title}}: {{count}} category, in {{unit}}',
      summary_other: '{{title}}: {{count}} categories, in {{unit}}',
      summaryStacked_one:
        '{{title}}: {{count}} category, in {{unit}}. Stacked by {{names}}.',
      summaryStacked_other:
        '{{title}}: {{count}} categories, in {{unit}}. Stacked by {{names}}.',
      // A grouped bar chart: each item's series side by side.
      summaryCompared_one:
        '{{title}}: {{count}} item, in {{unit}}. Compares {{names}}.',
      summaryCompared_other:
        '{{title}}: {{count}} items, in {{unit}}. Compares {{names}}.',
      // A 100% bar: each item's parts as shares of it.
      summaryShares_one: '{{title}}: {{count}} item, split into {{names}}.',
      summaryShares_other:
        '{{title}}: {{count}} items, each split into {{names}}.',
      // A donut: one whole's parts.
      summaryParts_one: '{{title}}: {{count}} part, in {{unit}}.',
      summaryParts_other: '{{title}}: {{count}} parts, in {{unit}}.',
      // A panel of charts read together: what it shows and its unit.
      panelTitle: '{{title}} ({{unit}})',
      // A sparkline table's columns: the periods its trends run over, a
      // period's total, and the change from the first total to the last.
      trend: '{{from}}–{{to}}',
      periodTotal: '{{period}} total ({{unit}})',
      change: 'Change',
      // A waterfall: its steps from start to total, and its legend.
      summarySteps_one: '{{title}}: {{count}} step, in {{unit}}.',
      summarySteps_other: '{{title}}: {{count}} steps, in {{unit}}.',
      increase: 'Increase',
      decrease: 'Decrease',
      // A progress bar: how far there is still to go to its target, in
      // points for a percentage, and what it says to assistive tech.
      pointsToGo_one: '{{value}} point to go',
      pointsToGo_other: '{{value}} points to go',
      toGo: '{{value}} to go',
      targetReached: 'Target reached',
      // Where nothing says which way a figure moves to its target: only how
      // far it is from it.
      pointsFromTarget_one: '{{value}} point from the target',
      pointsFromTarget_other: '{{value}} points from the target',
      fromTarget: '{{value}} from the target',
      atTarget: 'At the target',
      summaryProgress:
        '{{title}}: now {{current}}, target {{target}}, {{standing}}.',
      summaryExpected: '{{label}}: {{value}}.',
      summaryProgressFrom:
        '{{title}}: from {{start}} to {{target}}, now {{current}}, {{standing}}.',
      // A target line chart: what its planned path is.
      straightLinePath: 'Straight-line path',
      // A coverage grid: whether each item's period was received, a row's
      // count of those received, and the two states in its legend.
      cellReceived: '{{item}}, {{period}}: received',
      cellMissing: '{{item}}, {{period}}: missing',
      coverageCount: '{{received}}/{{total}}',
      received: 'Received',
      missing: 'Missing',
      // The control switching between a switchable chart's charts, and a chart in it
      // the reply left untitled, named by its place.
      views: 'Chart views',
      view: 'View {{n}}',
      // A missing value — shown as a gap, never as zero.
      noData: 'No data',
      empty: 'No data to chart.',
      // One dimension a chart holds to a single value: "site: Amiens".
      slice: '{{dimension}}: {{value}}',
      // The line under a chart's title where its marks don't head their own
      // unit: the unit and the slice held fixed. Under it, where clicking a
      // mark opens its sources, a line saying so.
      inUnit: 'In {{unit}}',
      inPercent: 'In percent',
      hintBars: 'Click any bar for its sources.',
      hintPoints: 'Click any point for its sources.',
      hintEnds: 'Click the end of a line for its sources.',
      // A line's latest value and its name, at its end.
      lineEnd: '{{value}} {{name}}',
      hintParts: 'Click any part for its sources.',
      hintCells: 'Click any cell for its sources.',
      hintRows: 'Click a row for its sources.',
      hintFigures: 'Click a figure for its sources.',
      // In place of the hint while one of the chart's values is open.
      hintClear: 'Click the selection again, or press Esc, to clear it.',
      // Copying a chart as a picture, and what it did.
      copyImage: 'Copy image',
      imageSaved: 'Saved as a file',
      imageFailed: 'Could not copy',
      // A chart's foot: how much of what was expected its values cover, and
      // the key to the marks for estimated and missing values.
      source: 'Source: {{source}}',
      coverage: 'Coverage {{share}} of expected volume.',
      keyEstimated: 'Hatched: estimated.',
      keyMissing: 'Dashed: expected but missing, not counted as zero.',
      // A value with the part of it that is estimated, and what was expected
      // but is missing.
      withEstimate: '{{value}}, {{estimate}} estimated',
      expectedMissing: 'Expected but missing',
      // How a ranked row's figure was got, as far as the data says.
      basisActual: 'Actual',
      basisEstimated: 'Estimated',
      basisPartlyEstimated: 'Partly estimated',
      category: 'Category',
      total: 'Total',
      // A stat tile's figure as one phrase for assistive tech.
      statSummary: '{{label}}: {{value}}',
      // A percentage's change, which is in points.
      changePoints_one: '{{change}} point',
      changePoints_other: '{{change}} points',
      // A heatmap's cell: an item's value in a period, or that it has none,
      // and where it is, heading its read-out.
      cellValue: '{{item}}, {{period}}: {{value}}',
      cellPlace: '{{item}} · {{period}}',
      cellNoData: '{{item}}, {{period}}: no data',
    },
    // The column a finding opens in, from the underlined words in a planned
    // reply that state it.
    provenance: {
      // How Annwn worked out a calculated figure (`CalculationWorking`).
      calculation: {
        // `steps` and `figures` are the counts below, already worded.
        summary: 'Worked out in {{steps}} from {{figures}} in your documents',
        // When every figure that went in was one Annwn stated.
        summaryStated: 'Worked out in {{steps}}',
        steps_one: '{{count}} step',
        steps_other: '{{count}} steps',
        figures_one: '{{count}} figure',
        figures_other: '{{count}} figures',
        showAll: 'Show all {{count}} steps',
        showFewer: 'Show fewer steps',
        // Steps are numbered in the order the working was done, the figure's
        // own last. Above each step: its number and operation.
        heading: 'Step {{number}} · {{operation}}',
        operations: {
          add: 'Sum',
          subtract: 'Difference',
          multiply: 'Product',
          divide: 'Division',
          percentage_of: 'Percentage of',
          percentage_change: 'Percentage change',
          convert: 'Conversion',
        },
        // What one figure of a breakdown is called below its own step's rule;
        // a whole finding's figure is called by its label.
        valueName: '{{label}}, {{place}}',
        // On an input worked out in another step; opens that step.
        step: 'Step {{number}}',
        // What an input is called when Annwn gave it no words.
        unnamed: {
          record: 'Figure',
          step: 'Worked-out figure',
          factor: 'Factor',
          // The factor a conversion multiplied by: a standard conversion
          // between units, such as 3.6 GJ per MWh.
          conversion: 'Standard conversion',
        },
        showWorkingLabel: 'Show how {{name}} was worked out, step {{number}}',
        hideWorkingLabel: 'Hide how {{name}} was worked out, step {{number}}',
        source: 'Source',
        sourceLabel: 'Open the passage {{name}} was read from',
        stated: 'Stated',
        statedLabel: 'Stated by Annwn, not read from your documents',
        standard: 'Standard',
        standardLabel:
          'A standard conversion between units, not read from your documents',
        showMore: 'Show {{count}} more',
        showFewerInputs: 'Show fewer',
      },
      sources: {
        loadFailed: 'The source documents could not be loaded.',
      },
    },
    failure: {
      title: 'Annwn couldn’t finish this answer.',
      fallback: 'Something went wrong finding your answer.',
      notQueued:
        'Annwn couldn’t pick that up — something went wrong before it started. Nothing about your data has changed, so send it again.',
      llmProviderError:
        'Annwn could not reach its model provider. Nothing was saved.',
      persistError: 'Annwn found an answer but could not save it.',
      internalError: 'Something went wrong finding your answer.',
      // Sends the same question again as a new run; the failed one stays failed.
      retry: 'Ask this question again',
      // The status word, then the step the run stopped on as the progress
      // list names it.
      failedStep: '<status>Failed</status> · {{step}}',
    },
    // Heads the way out of a reply that reached no answer.
    nextSteps: 'Next steps',
    // A settled answer's card: a strip saying how Annwn worked, then labelled
    // sections — the response, what to trust about it, and what it means for
    // the reader with what to ask next.
    answer: {
      sections: {
        response: 'Response',
        trust: 'Trust',
        soWhat: 'So what',
      },
      // What a suggested follow-up sets out to do, beside the question.
      followUpKind: {
        investigate: 'Investigate',
        reconcile: 'Reconcile',
        plan: 'Plan',
        explore: 'Explore',
        compare: 'Compare',
        check: 'Check',
        estimate: 'Estimate',
        act: 'Act',
      },
      // The rows of the "So what" card on acting on an answer.
      consideration: {
        drivers: 'What drives it',
        levers: 'How you’d reduce it',
        obstacles: 'What makes it hard',
        knock_on_effects: 'Knock-on effects',
      },
      workedFor: 'Worked for {{time}}',
      stoppedAfter: 'Stopped after {{time}}',
      agents_one: '{{count}} agent',
      agents_other: '{{count}} agents',
      flagged_one: '{{count}} flagged',
      flagged_other: '{{count}} flagged',
      howAnnwnGotHere: 'How Annwn got here',
      // The trust cards: each opens the evidence column at its section.
      cards: {
        missing: 'Data quality',
        assumptions: 'Assumptions',
        sources: 'Sources',
      },
      sourcesCited_one: '{{count}} source cited',
      sourcesCited_other: '{{count}} sources cited',
      reasoningOnly: 'Annwn’s reasoning, with no sources cited',
      noSourcesCited: 'No sources cited',
      // The documents the sources came from, where too many to name.
      documentsCited_one: '{{count}} document from your vault',
      documentsCited_other: '{{count}} documents from your vault',
      // A declined reply's cards: it gave no figure, so none of them applied.
      nothingSuitable: 'Nothing suitable found',
      uploadToAnswer: 'Upload a document to answer this',
      nothingToRate: 'No figure given, so nothing to rate',
      noneApplied: 'None applied',
      noneAppliedNote: 'Nothing on file to apply them to',
      // The worst data-quality item, under the count: "Missing data: Thetford".
      issueLine: '{{kind}}: {{affects}}',
    },
    // The one column a reply's evidence opens in, at the whole answer's scope
    // or narrowed to one figure or claim of it.
    basis: {
      // The counts on the trust cards and atop the data-quality tab.
      // Zero when the data quality holds only its coverage.
      dataIssueCount_zero: 'No data issues',
      dataIssueCount_one: '{{count}} data issue',
      dataIssueCount_other: '{{count}} data issues',
      // A choice as the agent wrote it: its name, then its value.
      choice: '{{name}}: {{value}}',
      // Over each group of assumptions: where they came from, as what it
      // means for the reader.
      group: {
        default: 'Annwn chose these. You can change them.',
        analysis: 'Annwn assumed these while working out the figures.',
        request: 'Taken from your question.',
        records: 'Taken from your records.',
      },
      // A note's kind, said to assistive tech ahead of what it is about.
      kindPrefix: '{{kind}}: ',
      kind: {
        coverage: 'Coverage',
        gap: 'Missing data',
        estimate: 'Estimate',
        mixed_basis: 'Mixed basis',
        conflict: 'Conflicting records',
        other: 'Other',
      },
    },
    evidence: {
      close: 'Close',
      scope: 'Scope',
      answer: 'This answer',
      // The figure or claim the reader selected, set out over the tabs, and
      // the chart it was drawn in, which shows it there.
      selected: 'You selected',
      fromChart: 'from <chart>{{chart}}</chart>',
      // Over a figure's assumptions and data notes: the reply doesn't say
      // which bear on which figure.
      wholeAnswer: 'Applies to the whole answer',
      declined: {
        label: 'No answer',
        reason: {
          insufficient_data: 'Not enough on file',
          out_of_scope: 'Outside what Annwn covers',
          ambiguous_question: 'Question unclear',
          no_applicable_concept: 'A topic Annwn doesn’t cover yet',
          history_exhausted: 'Conversation too long',
          could_not_verify: 'Answer couldn’t be checked',
          other: 'Declined',
        },
      },
      // The column's four tabs, in the order they read.
      tabs: {
        label: 'How Annwn reached this',
        working: 'How it worked',
        sources: 'Sources',
        assumptions: 'Assumptions',
        missing: 'Data quality',
      },
      // What the tabs hold, at the whole answer's scope or narrowed to one
      // figure. Every sentence is made from what the reply recorded.
      story: {
        title: 'How Annwn reached this',
        // A step that didn't finish.
        stopped: {
          failed: 'It stopped here.',
          blocked: 'It waited here for your answer.',
        },
        // The figures the reply worked out, each opening its working.
        figures: 'Figures worked out',
        // A figure the reply worked out, by the last step of its calculation.
        // `label` is the finding's, `value` the figure with its unit.
        calc: {
          add_one: 'Added {{count}} figure for {{label}}: {{value}}',
          add_other: 'Added {{count}} figures for {{label}}: {{value}}',
          subtract: 'Took one figure from another for {{label}}: {{value}}',
          multiply_one: 'Multiplied {{count}} figure for {{label}}: {{value}}',
          multiply_other:
            'Multiplied {{count}} figures for {{label}}: {{value}}',
          divide: 'Divided one figure by another for {{label}}: {{value}}',
          percentage_of:
            'Took one figure as a share of another for {{label}}: {{value}}',
          percentage_change:
            'Took the change from one figure to another for {{label}}: {{value}}',
          convert:
            'Converted a figure to another unit for {{label}}: {{value}}',
        },
        // A step's searches, each in the agent's words.
        searches_one: 'The search',
        searches_other: 'The {{count}} searches',
        groupRecords_one: '{{formatted}} record came back',
        groupRecords_other: '{{formatted}} records came back',
        groupNothing: 'Nothing came back',
        // The findings a step wrote, each under its label.
        metrics_one: 'The metric',
        metrics_other: 'The {{count}} metrics',
        // The reply's reasoning, in the agent's own words.
        inWords: 'In Annwn’s words',
        // A tab with nothing to show.
        empty: {
          working: 'Annwn recorded no steps for this answer.',
          assumptions: 'Annwn recorded no choices for this answer.',
          missing: 'Nothing was flagged for this answer.',
        },
        // Data quality.
        missing: {
          nothingHere: 'Nothing was flagged for this figure.',
          noValue: '{{finding}} has no figure for it.',
        },
      },
      // One file the answer used, opened on Sources: what was read from it
      // and what it went into.
      files: {
        read: 'Read from it',
        wentInto: 'Went into',
        showRead: 'What was read from {{filename}}',
        // A file the answer read whose records went into none of its
        // findings.
        noFigure: 'Read, but no figure in this answer comes from it',
        // A file the answer read that says nothing else of itself.
        readFor: 'Read for this answer',
        whole: 'Cited as a whole, with no record picked out.',
      },
      // One figure traced to its sources, on Sources.
      ledger: {
        made: 'How it was made',
        sources: 'Read from',
        gaps: 'Gaps',
        noSources: 'No source was recorded for this figure.',
        noneOnFile: 'None on file',
        all_one: 'Show the {{count}} source',
        all_other: 'Show all {{count}} sources',
        fewer: 'Show fewer',
        // The last step of a figure's calculation.
        operation: {
          add_one: 'The sum of {{count}} figure.',
          add_other: 'The sum of {{count}} figures.',
          subtract: 'One figure less another.',
          multiply_one: 'The product of {{count}} figure.',
          multiply_other: 'The product of {{count}} figures.',
          divide: 'One figure divided by another.',
          percentage_of: 'One figure as a share of another.',
          percentage_change: 'The change from one figure to another.',
        },
      },
      // A figure's trail, from the figure to the files behind it.
      trail: {
        worked: 'How it was worked out',
        // A claim states a fact rather than a figure.
        reached: 'How it was reached',
        reported: 'Where it was reported',
        // A value's whole finding: `label` is the finding's.
        whole: 'About {{label}}',
        breakdown: 'Breakdown',
        // A figure of the breakdown the records held nothing for.
        noFigure: 'None on file',
      },
    },
    // The evidence column's shared copy: assumptions and their alternatives,
    // the data-quality actions, and the rules.
    decline: {
      fallback: "I can't answer that one.",
      insufficientData:
        "I don't have enough data in this account to answer that yet.",
      outOfScope: 'That falls outside what I can help with here.',
      ambiguousQuestion:
        "I'm not sure what you're asking — try narrowing it down.",
      other: "I can't answer that one.",
      historyExhaustedTitle: 'Annwn can’t continue this conversation.',
      // The reader has to know nothing carries over: a new chat starts from
      // their first message, so anything from here that still matters has to be
      // restated.
      historyExhausted:
        'This conversation has got too long for Annwn to answer reliably. Start a new chat — nothing carries over, so include any detail from here that still matters.',
      // Annwn only answers on topics it has guidance for, and that set grows
      // over time, so the copy names no topic.
      noApplicableConcept:
        "I can't help with that topic yet. I cover a small set of sustainability topics so far, and more are on the way.",
      couldNotVerify:
        "I couldn't check this answer well enough to give it to you. Try asking again, or make the question more specific.",
      startNewChat: 'Start new chat',
    },
    options: {
      title: 'Run options',
      internalBadge: 'Internal',
      description:
        'Applies to your own messages from this browser, and persists until you change it.',
      // Each choice's `hint` is a one- or two-word qualifier shown beside it in
      // the open list, so the choices compare at a glance.
      toolEffort: 'Tool effort',
      toolEfforts: {
        low: { label: 'Low', hint: 'Fewest lookups' },
        medium: { label: 'Medium', hint: 'Balanced' },
        high: { label: 'High', hint: 'Most thorough' },
      },
      // "Thinking" is the model's hidden deliberation before it replies — not
      // "reasoning", which is the explanation a reply shows its reader.
      thinkingLevel: 'Thinking',
      thinkingLevels: {
        default: { label: 'Default', hint: 'Worker’s level' },
        low: { label: 'Low', hint: 'Fastest' },
        medium: { label: 'Medium', hint: 'Usual' },
        high: { label: 'High', hint: 'Most thorough' },
      },
      // The steps that check each stage's work before the reply goes on.
      review: 'Review',
      reviews: {
        on: { label: 'On', hint: 'Checks each step' },
        off: { label: 'Off', hint: 'Faster, unchecked' },
      },
    },
  },
  // The strip above the app outside production.
  environment: {
    hide: 'Hide environment strip',
  },
  support: {
    title: 'Support',
    gettingStartedLabel: 'Getting started',
    gettingStartedIntro:
      'New to Annwn? Here’s the path from signing in to a finished deliverable.',
    steps: {
      account:
        'Open or create an account for the organisation or entity you’re reporting for.',
      workspace:
        'Create a workspace for the report, questionnaire, or briefing you need to produce.',
      documents:
        'Upload your source documents. Annwn reads them into your organisation’s knowledge graph so answers stay grounded in your own evidence.',
      draft:
        'Ask the Annwn agent to draft answers, then review its responses, reasoning, and cited sources.',
      deliver:
        'Finalise with your team and export the deliverable once it’s signed off.',
    },
    faqsLabel: 'Frequently asked questions',
    faqs: {
      workspace: {
        q: 'What is a workspace?',
        a: 'A workspace is where one job of work gets done — from a quick board briefing to a full CDP submission. It holds the scope, evidence, agent team and deliverable for that job in one place.',
      },
      documents: {
        q: 'How do I add documents to a workspace?',
        a: 'Open a workspace and go to Documents, then drag and drop your files or click to browse. Annwn processes each upload into your knowledge graph so it can draft answers from your annual report, prior CDP responses and more.',
      },
      drafting: {
        q: 'How does AI drafting work?',
        a: 'On any question you can ask the Annwn agent to draft an answer. It gathers evidence from your documents, proposes a response with its reasoning and sources, and flags anything that needs your review before you accept it.',
      },
      review: {
        q: 'How do I review and finalise answers?',
        a: 'Each drafted answer shows its confidence, reasoning, and the sources it used, so you can check it before accepting. Mark a question as ready for review to flag it for your team’s sign-off, then export the workspace’s deliverables once everything is settled.',
      },
      knowledgeGraph: {
        q: 'What is the knowledge graph?',
        a: 'Every document you upload is turned into a structured knowledge graph for your organisation. Workspaces share it, so an answer produced once can be reused across reports — always traceable back to its source.',
      },
      access: {
        q: 'How do I get access or add a teammate?',
        a: 'Access is managed by your organisation administrator. If you need an account or want to invite a colleague, email us and we’ll help get them set up.',
      },
    },
    contact: {
      heading: 'Still need help?',
      body: 'Can’t find what you need? Email our team and we’ll get back to you.',
      email: 'support@annwn.ai',
    },
  },
  whatsNew: {
    title: 'What’s new',
    releases: {
      askAnnwn: {
        date: 'September 2026',
        title: 'Ask Annwn about your data',
        items: {
          one: 'Ask a question about your sustainability data in plain English, from the Ask Annwn page or your dashboard. Energy consumption is the first topic it covers in depth.',
          two: 'A team of agents shapes the question, curates the evidence, analyses the figures and writes the answer, and you can watch them work.',
          three:
            'Every figure comes with its sources, the assumptions made and any gaps in the data, so you can check it before you use it. Your threads are kept, so you can pick a question up again.',
        },
      },
      cdpDrafting: {
        date: 'June 2026',
        title: 'Draft CDP responses with AI',
        items: {
          one: 'Draft answers to CDP questions with the Annwn agent — it draws on your uploaded documents and last year’s responses.',
          two: 'Every draft comes with a confidence level, its reasoning, and citations back to the source evidence.',
          three:
            'Review AI suggestions inline and accept or reject them; your feedback sharpens future drafts.',
        },
      },
    },
  },
} as const

export type Translations = typeof en
